import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
// @ts-ignore
import { createPortal } from 'react-dom';
import { X, Mail, Phone, MapPin, Calendar, FileText, MessageSquare, Send, Paperclip, Download, Trash2, ExternalLink, Linkedin, CheckCircle2, AlertCircle, Loader2, Briefcase, GraduationCap, Wrench, User, Users, BrainCircuit, Star, ArrowRight, CheckSquare, ChevronDown, Cloud, Activity, StickyNote, Inbox, Sparkles, Search, Plus } from 'lucide-react';
import { supabase_get_candidate_profile_by_id, supabase_get_documents, supabase_get_candidate_communications, supabase_save_candidate_communication, supabase_get_candidate_reviews, supabase_update_application_status, supabase, supabase_update_candidate, generateCandidateSlug } from '../services/supabaseService';
import { performRealMatch, generateTemplateContent } from '../services/geminiService';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';

import { uploadToOneDrive, getOneDriveFolderUrl } from '../services/microsoftGraphService';
import { generateWordDocument, fetchTemplateBuffer } from '../services/wordTemplateService';
import { VacancyStage, BULLHORN_STATUS_OPTIONS, UserRole } from '../types';
import { CallSidebar } from './CallSidebar';
import BulkEmailComposerModal from './BulkEmailComposerModal';
import { BulkWhatsAppComposerModal } from './BulkWhatsAppComposerModal';
import TeamsMeetingScheduler from './TeamsMeetingScheduler';
import FeedbackScorecard from './FeedbackScorecard';
import { CandidateNotes } from './CandidateNotes';
import { DebugTag } from './DebugTag';
import { isPortalHideCandidateNameForStatus, portalPrivacyFromUser } from '../utils/clientPortalGuards';

function normalizeWorkRow(item: any) {
    return {
        role: String(item?.role || item?.title || item?.functie || ''),
        company: String(item?.company || item?.bedrijf || ''),
        period: String(item?.period || item?.year || item?.periode || ''),
        description: String(item?.description || item?.beschrijving || ''),
    };
}

function normalizeEduRow(item: any) {
    return {
        degree: String(item?.degree || item?.opleiding || item?.opleiding_naam || item?.title || ''),
        school: String(item?.school || item?.instituut || ''),
        year: String(item?.year || item?.periode || ''),
    };
}

/** Beschikbaarheid met volledige maandnaam (NL) */
function formatAvailabilityHuman(c: any, parsed: any): string | null {
    const raw =
        parsed?.available_from ||
        parsed?.availableFrom ||
        c?.ai_parsed_data?.available_from ||
        c?.available_from;
    if (raw) {
        const s = String(raw);
        const d = new Date(s.length <= 10 ? `${s}T12:00:00` : s);
        if (!isNaN(d.getTime())) {
            return d.toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' });
        }
    }
    const av = c?.availability;
    if (av != null && String(av).trim()) return String(av).trim();
    return null;
}

interface CandidateDetailModalProps {
    candidateId: string;
    vacancyId?: string;
    clientId: string;
    currentUser?: any;
    stages?: VacancyStage[];
    onUpdate?: () => void;
    onScheduleClick?: () => void;
    onClose: () => void;
    initialTab?: 'overview' | 'documents' | 'notes' | 'activity' | 'interviews' | 'reviews' | 'ai_analysis' | 'comm' | 'raw_cv' | 'vacatureflow';
    initialAction?: 'whatsapp' | 'email';
    /** Client portal: block all writes (candidate, application, comms, etc.). */
    forceReadOnly?: boolean;
}

const CandidateDetailModal: React.FC<CandidateDetailModalProps> = ({ candidateId, vacancyId, clientId, currentUser, stages, onUpdate, onScheduleClick, onClose, initialTab = 'overview', initialAction, forceReadOnly = false }) => {
    const user = currentUser;
    const isClientPortalHost =
        typeof window !== 'undefined' &&
        (window.location.hostname.toLowerCase().includes('clientportal') ||
            new URLSearchParams(window.location.search).get('clientportal') === 'true');
    /** Alleen «klant»-rol; CLIENT_RECRUITER is een recruiter-account en moet op werftools.nl het volledige dossier hebben. */
    const isClientRole = user?.role === UserRole.CLIENT;
    const isClientPortalUser = user?.role === UserRole.CLIENT || user?.role === UserRole.CLIENT_RECRUITER;
    const portalPrivacy = portalPrivacyFromUser(user);
    /** Bewerken blokkeren: expliciet portaal-host, forceReadOnly, of pure CLIENT (niet client-recruiter op hoofddomein). */
    const isReadOnlyPortal = forceReadOnly === true || isClientPortalHost || isClientRole;
    /** Tabs beperken: alleen echt klantportaal-venster of recruiter-preview (forceReadOnly), niet op basis van CLIENT_RECRUITER-rol alleen. */
    const portalLimitedTabs = forceReadOnly === true || isClientPortalHost;
    const blockPortalMutation = (message = 'Bewerken is niet beschikbaar in het clientportaal.') => {
        if (!isReadOnlyPortal) return false;
        if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('wt:toast', { detail: { message, type: 'error' } }));
        }
        return true;
    };
    // Normalize virtual vacancy IDs so they never reach UUID columns
    const safeVacancyId = (!vacancyId || vacancyId === 'ALL' || vacancyId.startsWith('ALL_')) ? undefined : vacancyId;
    const validTabs = new Set([
        'overview',
        'raw_cv',
        'documents',
        'notes',
        'activity',
        'comm',
        'interviews',
        'reviews',
        'ai_analysis',
        'vacatureflow',
    ]);
    const safeInitial = (initialTab && validTabs.has(initialTab) ? initialTab : 'overview') as
        | 'overview'
        | 'raw_cv'
        | 'documents'
        | 'notes'
        | 'activity'
        | 'comm'
        | 'interviews'
        | 'reviews'
        | 'ai_analysis'
        | 'vacatureflow';
    const [activeTab, setActiveTab] = useState<
        'overview' | 'raw_cv' | 'documents' | 'notes' | 'activity' | 'comm' | 'interviews' | 'reviews' | 'ai_analysis' | 'vacatureflow'
    >(safeInitial);
    const [profile, setProfile] = useState<any>(null);
    const [editRawCv, setEditRawCv] = useState('');
    const [isEditingRawCv, setIsEditingRawCv] = useState(false);
    const [isSavingRawCv, setIsSavingRawCv] = useState(false);
    const [documents, setDocuments] = useState<any[]>([]);
    const [communications, setCommunications] = useState<any[]>([]);
    const [reviews, setReviews] = useState<any[]>([]);
    const [interviews, setInterviews] = useState<any[]>([]);
    const [applications, setApplications] = useState<any[]>([]);
    const [allVacancies, setAllVacancies] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    const [showVacancySelector, setShowVacancySelector] = useState(false);
    const [isLinkingVacancy, setIsLinkingVacancy] = useState(false);

    // Actions
    const [showScheduler, setShowScheduler] = useState(false);
    const [currentStageId, setCurrentStageId] = useState<string>('');
    const [isUpdatingStage, setIsUpdatingStage] = useState(false);
    const [showTodoInput, setShowTodoInput] = useState(false);
    const [newTodo, setNewTodo] = useState('');
    const [isSavingTodo, setIsSavingTodo] = useState(false);

    // Message State
    const [newMessage, setNewMessage] = useState('');
    const [isSending, setIsSending] = useState(false);
    const messagesEndRef = useRef<HTMLDivElement>(null);

    // Call Feature State
    const [showCallModal, setShowCallModal] = useState(false);
    const [editCommId, setEditCommId] = useState<string | undefined>(undefined);
    const [editCommContent, setEditCommContent] = useState<string | undefined>(undefined);
    const [showMailBuilder, setShowMailBuilder] = useState(false);
    const [showWhatsAppBuilder, setShowWhatsAppBuilder] = useState(false);
    const [mailPrompt, setMailPrompt] = useState('');
    const [generatedMail, setGeneratedMail] = useState('');
    const [isGeneratingMail, setIsGeneratingMail] = useState(false);

    // Initial Action Auto-trigger
    useEffect(() => {
        if (isReadOnlyPortal) return;
        if (initialAction === 'whatsapp') {
            setActiveTab('activity');
            setShowWhatsAppBuilder(true);
        } else if (initialAction === 'email') {
            setActiveTab('activity');
            setShowMailBuilder(true);
        }
    }, [initialAction, isReadOnlyPortal]);

    // TAGS EDITING STATE
    const [isEditingTags, setIsEditingTags] = useState(false);
    const [editTagsInput, setEditTagsInput] = useState('');
    const [isSavingTags, setIsSavingTags] = useState(false);
    const [recruiterPhone, setRecruiterPhone] = useState(user?.phone || '');
    const [isCalling, setIsCalling] = useState(false);

    // AI Analysis State
    const [isAnalyzing, setIsAnalyzing] = useState(false);

    // Contact Edit State
    const [vacancySearch, setVacancySearch] = useState('');
    const [isGeneratingDoc, setIsGeneratingDoc] = useState(false);
    const [isEditingContact, setIsEditingContact] = useState(false);
    const [editPhone, setEditPhone] = useState('');
    const [editEmail, setEditEmail] = useState('');
    const [editSourceInput, setEditSourceInput] = useState('');
    const [editAvailableFrom, setEditAvailableFrom] = useState('');
    const [editEmploymentPreference, setEditEmploymentPreference] = useState('');
    const [isSavingContact, setIsSavingContact] = useState(false);
    const [showTeamsScheduler, setShowTeamsScheduler] = useState(false);

    const [editingMotivation, setEditingMotivation] = useState(false);
    const [motivationDraft, setMotivationDraft] = useState('');
    const [savingMotivation, setSavingMotivation] = useState(false);
    const [editingWork, setEditingWork] = useState(false);
    const [workDraft, setWorkDraft] = useState<Array<{ role: string; company: string; period: string; description: string }>>([]);
    const [savingWork, setSavingWork] = useState(false);
    const [editingEdu, setEditingEdu] = useState(false);
    const [eduDraft, setEduDraft] = useState<Array<{ degree: string; school: string; year: string }>>([]);
    const [savingEdu, setSavingEdu] = useState(false);

    // Reviews State
    const [newReviewScore, setNewReviewScore] = useState(0); // 0-10
    const [newReviewText, setNewReviewText] = useState('');
    const [isSubmittingReview, setIsSubmittingReview] = useState(false);

    // OneDrive URL state
    const [oneDriveUrl, setOneDriveUrl] = useState<string | null>(null);

    // Deep Sync State
    const [isDeepSyncing, setIsDeepSyncing] = useState(false);
    
    // Restart Mapping State
    const [isRestartingMapping, setIsRestartingMapping] = useState(false);

    // Handmatig inkomend loggen (plakken uit WhatsApp Web / e-mail)
    const [inboundWaPaste, setInboundWaPaste] = useState('');
    const [inboundEmailPaste, setInboundEmailPaste] = useState('');
    const [savingInboundWa, setSavingInboundWa] = useState(false);
    const [savingInboundEmail, setSavingInboundEmail] = useState(false);
    const inboundLogRef = useRef<HTMLDivElement>(null);

    useLayoutEffect(() => {
        if (!portalLimitedTabs) return;
        setActiveTab((tab) => (tab === 'overview' || tab === 'documents' ? tab : 'overview'));
    }, [portalLimitedTabs]);

    useEffect(() => {
        if (!isReadOnlyPortal) return;
        setIsEditingContact(false);
        setEditingMotivation(false);
        setEditingWork(false);
        setEditingEdu(false);
        setIsEditingTags(false);
        setIsEditingRawCv(false);
        setShowTodoInput(false);
        setShowVacancySelector(false);
        setShowScheduler(false);
    }, [isReadOnlyPortal]);

    const getDocumentUrl = (url?: string) => {
        if (!url) return '#';
        const lower = url.toLowerCase();
        if (lower.includes('.doc') || lower.includes('.docx') || lower.includes('.rtf') || lower.includes('.ppt')) {
            return `https://view.officeapps.live.com/op/view.aspx?src=${encodeURIComponent(url)}`;
        }
        return url;
    };

    const handleRestartMapping = async () => {
        if (blockPortalMutation()) return;
        setIsRestartingMapping(true);
        try {
            const rawMaterial = profile?.candidate?.original_cv_text || profile?.candidates?.original_cv_text || '';
            let documentUrl = '';

            if (documents && documents.length > 0) {
                documentUrl = documents[0].url; // Usually the CV is the first document
            }

            if (!rawMaterial && !documentUrl) {
                alert("Geen bron CV tekst of CV document gevonden om opnieuw af te vangen met AI.");
                setIsRestartingMapping(false);
                return;
            }

            const { analyzeUploadedCV } = await import('../services/geminiService');
            let analyzeResult;

            if (rawMaterial) {
                // If we have HTML or raw text stored
                analyzeResult = await analyzeUploadedCV(rawMaterial);
            } else if (documentUrl) {
                // Try fetching the document
                try {
                    const fileRes = await fetch(documentUrl);
                    if (!fileRes.ok) throw new Error("Network response was not ok");
                    const blob = await fileRes.blob();
                    const reader = new FileReader();
                    const base64Promise = new Promise<string>((resolve) => {
                        reader.onloadend = () => {
                            const result = reader.result as string;
                            resolve(Array.isArray(result) ? result[0] : (result.split(',')[1] || result));
                        };
                        reader.readAsDataURL(blob);
                    });
                    const base64 = await base64Promise;
                    analyzeResult = await analyzeUploadedCV({ base64, mimeType: blob.type });
                } catch (fetchErr) {
                    console.error("Document fetch error", fetchErr);
                    alert("Kon document niet inlezen (waarschijnlijk vanwege beveiligingsinstellingen / CORS). Handmatige mapping vanaf tekst is nodig.");
                    setIsRestartingMapping(false);
                    return;
                }
            }

            if (!analyzeResult) throw new Error("Ongeldige response van AI");

            const updatedAiData = {
                ...(c.ai_parsed_data || {}),
                ...analyzeResult
            };

            const cData = profile.candidate || profile.candidates || {};

            const { error: pErr } = await supabase.from('candidates')
                .update({ ai_parsed_data: updatedAiData })
                .eq('id', c.id);

            await supabase.from('candidates')
                .update({ 
                    cv_summary: analyzeResult.summary,
                    skill_set: JSON.stringify(analyzeResult.tags || []),
                    experience: JSON.stringify(analyzeResult.timeline || [])
                 })
                .eq('id', cData.id);

            if (pErr) throw pErr;

            alert("Her-mapping voltooid! Het profiel is succesvol bijgewerkt.");
            loadData();
        } catch (e: any) {
            console.error("Re-mapping failed", e);
            alert("Fout bij re-mapping (Kijk na of Gemini Key actief is): " + e.message);
        } finally {
            setIsRestartingMapping(false);
        }
    };

    const handleDeepSync = async () => {
        if (blockPortalMutation()) return;
        const bullhornId = parsed.id;
        const authData = {
            restUrl: user?.bullhorn_settings?.restUrl,
            BhRestToken: user?.bullhorn_settings?.BhRestToken
        };

        if (!bullhornId || !authData.restUrl) {
            alert("Kan niet syncen: Kandidaat mist Bullhorn ID of abonnementgegevens zijn niet compleet.");
            return;
        }

        setIsDeepSyncing(true);
        try {
            const { fetchBullhornCandidateFull, fetchBullhornCandidateNotes, fetchBullhornCandidateFiles, downloadBullhornCandidateFile } = await import('../services/bullhornService');
            const { supabase_upsert_imported_candidates, supabase_sync_bullhorn_notes, supabase_sync_bullhorn_documents } = await import('../services/supabaseService');

            const fullBhData = await fetchBullhornCandidateFull(authData, bullhornId);
            
            if (fullBhData.workHistories && fullBhData.workHistories.data) {
                fullBhData.timeline = fullBhData.workHistories.data.map((wh: any) => ({
                    company: wh.companyName || '',
                    role: wh.title || fullBhData.occupation || wh.companyName || '',
                    period: wh.startDate ? new Date(wh.startDate).getFullYear() + (wh.endDate ? ' - ' + new Date(wh.endDate).getFullYear() : ' - heden') : '',
                    description: wh.comments || ''
                }));
            }
            if (fullBhData.educations && fullBhData.educations.data) {
                fullBhData.education = fullBhData.educations.data.map((edu: any) => ({
                    school: edu.school || '',
                    degree: edu.degree || '',
                    year: edu.startDate ? new Date(edu.startDate).getFullYear() + (edu.endDate ? ' - ' + new Date(edu.endDate).getFullYear() : '') : ''
                }));
            }
            if (!fullBhData.summary && fullBhData.description && typeof fullBhData.description === 'string') {
                 fullBhData.summary = fullBhData.description.replace(/<[^>]*>?/gm, ''); 
            }
            if (fullBhData.skillSet && Array.isArray(fullBhData.skillSet)) {
                fullBhData.tags = fullBhData.skillSet;
            }

            await supabase_upsert_imported_candidates(clientId, [fullBhData]);

            try {
                const notes = await fetchBullhornCandidateNotes(authData, bullhornId);
                if (notes && notes.length > 0) {
                    await supabase_sync_bullhorn_notes(clientId, profile.id, user.id, notes);
                }
            } catch (e) { console.warn("Notes sync error", e); }

            try {
                const filesMeta = await fetchBullhornCandidateFiles(authData, bullhornId);
                const globalCandId = profile.candidate_id || profile.candidate?.id;
                if (filesMeta && filesMeta.length > 0 && globalCandId) {
                    const filesWithContent = [];
                    for (const fm of filesMeta) {
                        try {
                            const content = await downloadBullhornCandidateFile(authData, bullhornId, fm.id);
                            filesWithContent.push(content);
                        } catch (e) { console.warn("File download err", e); }
                    }
                    if (filesWithContent.length > 0) {
                        await supabase_sync_bullhorn_documents(clientId, globalCandId, filesWithContent);
                    }
                }
            } catch (e) { console.warn("Docs sync error", e); }

            alert("Deep Sync voltooid! Notities, CV elementen en Documenten zijn bijgewerkt.");
            loadData();
        } catch (e: any) {
            console.error(e);
            alert("Deep Sync Error: " + e.message);
        } finally {
            setIsDeepSyncing(false);
        }
    };

    useEffect(() => {
        loadData();
        getOneDriveFolderUrl('Kandidaten CVs').then(url => {
            if (url) setOneDriveUrl(url);
        });
    }, [candidateId, clientId]);

    useEffect(() => {
        if (activeTab === 'notes' || activeTab === 'activity') {
            scrollToBottom();
        }
    }, [communications, activeTab]);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };


    const handleGenerateWordCV = async () => {
        if (blockPortalMutation()) return;
        setIsGeneratingDoc(true);
        try {
            // 1. Fetch template URL from client settings
            const { data: clientData, error: clientErr } = await supabase
                .from('clients')
                .select('word_template_url')
                .eq('id', clientId)
                .single();

            if (clientErr || !clientData?.word_template_url) {
                alert("Er is geen Word Template gevonden voor jouw account. Stel deze eerst in via de Beheer / Instellingen pagina.");
                setIsGeneratingDoc(false);
                return;
            }

            // 2. Fetch the actual template file (buffer)
            // It could be a DataURL or a public Supabase URL
            let templateBuffer: ArrayBuffer;
            if (clientData.word_template_url.startsWith('data:')) {
                // Convert DataURL to ArrayBuffer
                const base64Parts = clientData.word_template_url.split(',');
                const binaryString = window.atob(base64Parts[1]);
                const bytes = new Uint8Array(binaryString.length);
                for (let i = 0; i < binaryString.length; i++) {
                    bytes[i] = binaryString.charCodeAt(i);
                }
                templateBuffer = bytes.buffer;
            } else {
                templateBuffer = await fetchTemplateBuffer(clientData.word_template_url);
            }

            // 3. Prepare data variables for docxtemplater
            // Extract cleanly from candidate profile and AI parsed data
            const c = profile?.candidate || profile?.candidates || {};

            // Safely extract and merge parsed data from all possible locations (same as UI render)
            let parsed: any = {};
            const sources = [profile?.parsed_cv, profile?.ai_parsed_data, c?.parsed_cv, c?.ai_parsed_data];
            sources.forEach(source => {
                if (source) {
                    try {
                        let data = source; while (typeof data === 'string') { try { data = JSON.parse(data); } catch(e) { break; } }
                        if (typeof data === 'object' && Object.keys(data).length > 0) {
                            parsed = { ...parsed, ...data };
                        }
                    } catch (e) { /* ignore parse error */ }
                }
            });
            if (parsed.bullhornData) {
                parsed = { ...parsed.bullhornData, ...parsed }; // Unpack nested bullhorn sync data
            }

            // Format work history into an array
            const workHistoryList = parsed.timeline || parsed.workHistory || parsed.work_experience || parsed.experience || parsed.werkervaring || [];
            const workHistory = workHistoryList.map((exp: any) => ({
                company: exp.company || exp.bedrijf || '',
                role: exp.role || exp.functie || exp.title || '',
                period: exp.period || exp.periode || exp.year || exp.date || '',
                description: exp.description || exp.omschrijving || exp.taken || exp.beschrijving || ''
            }));

            // Format education
            const educationList = parsed.education || parsed.educations || parsed.opleidingen || [];
            const educationArr = educationList.map((edu: any) => ({
                degree: edu.degree || edu.opleiding || edu.title || '',
                school: edu.school || edu.instituut || edu.institute || '',
                period: edu.year || edu.periode || edu.period || '',
                // Aliases specifically for current template:
                education: edu.degree || edu.opleiding || edu.title || '',
                educationschool: edu.school || edu.instituut || edu.institute || '',
                educationyear: edu.year || edu.periode || edu.period || ''
            }));

            // Format courses for [#course] loop
            const courses = (parsed.courses || parsed.certifications || parsed.trainingen || []).map((c: any) => ({
                coursetitel: c.name || c.course || c.title || '',
                courseinstitute: c.institute || c.school || c.instituut || '',
                courseyear: c.year || c.period || c.jaar || ''
            }));

            // Format eisen for [#eis] loop
            const eisen = (parsed.eisen || parsed.requirements || parsed.strong_points || []).map((r: any) => {
                if (typeof r === 'string') {
                    return { eistitel: r, eistekst: '' };
                }
                return {
                    eistitel: r.title || r.name || r.punt || r.eis || '',
                    eistekst: r.text || r.description || r.uitleg || r.tekst || ''
                };
            });

            // Format skills for [#skills] loop AND [#skill] loop
            const skillsArray = (parsed.skills || parsed.tags || []).map((s: any) => ({
                skilldetail: typeof s === 'string' ? s : (s.name || s.skilldetail || '')
            }));

            // Format wensen for [#wens] loop
            const wensen = (parsed.wensen || parsed.nice_to_haves || parsed.pluspunten || []).map((w: any) => {
                if (typeof w === 'string') {
                    return { wenstitel: w, wenstekst: '' };
                }
                return {
                    wenstitel: w.title || w.name || w.punt || w.wens || '',
                    wenstekst: w.text || w.description || w.uitleg || w.tekst || ''
                };
            });

            // Flatten skills into skill1, skill2 etc. for table-based column layouts
            const skillsFlat: Record<string, string> = {};
            skillsArray.forEach((s: any, index: number) => {
                skillsFlat[`skill${index + 1}`] = s.skilldetail;
            });

            const templateData = {
                name: c.name || '',
                email: c.email || '',
                phone: c.phone || '',
                role: c.role || parsed.role || '',
                location: parsed.personal_info?.location || parsed.location || c.city || '',
                summary: parsed.summary || parsed.profiel || c.cvSummary || '',
                skills: skillsArray,
                skill: skillsArray, // User used [#skill] instead of [#skills]
                workHistory: workHistory,
                education: educationArr,
                course: courses,
                eis: eisen,
                wens: wensen,
                ...skillsFlat
            };

            // 4. Generate the document Blob
            const newDocBlob = generateWordCVBytes(templateBuffer, templateData);

            // 5. Upload to OneDrive
            const candidateNameStr = c.name || 'Kandidaat';
            const fileName = `${candidateNameStr} CV.docx`.replace(/[\\/:*?"<>|]/g, '').replace(/\s+/g, ' ').trim();

            const { webUrl, error: uploadErr } = await uploadToOneDrive(newDocBlob, fileName);

            if (uploadErr) {
                console.error("OneDrive upload error", uploadErr);
                alert("Fout bij opslaan in de OneDrive: Microsoft account is mogelijk niet gekoppeld. Controleer je toegang via de Agenda tab of instellingen.");
            } else if (webUrl) {
                // Success! Open it
                window.open(webUrl, '_blank');

                // Track communication note
                if (user?.id && profile.real_profile_id) {
                    await supabase_save_candidate_communication(profile.real_profile_id, user.id, `Word CV Gegenereerd en opgeslagen in OneDrive (${fileName})`, 'note');
                    // Refresh data
                    loadData();
                }
            }

        } catch (e: any) {
            console.error("Fout bij Word CV Generatie", e);
            alert("Er is een fout opgetreden bij het genereren van het Word document: " + e.message);
        } finally {
            setIsGeneratingDoc(false);
        }
    };

    const handleSubmitReview = async () => {
        if (blockPortalMutation()) return;
        if (!newReviewScore) {
            alert("Kies eerst een beoordeling (1 tot 5 sterren).");
            return;
        }
        if (!vacancyId || !candidateId) {
            alert("Je kunt alleen een review achterlaten vanuit een actieve vacature.");
            return;
        }

        setIsSubmittingReview(true);
        try {
            const { data: userData } = await supabase.auth.getUser();
            const reviewerName = userData?.user?.user_metadata?.first_name || 'Gebruiker';
            
            const { error } = await supabase.from('candidate_reviews').insert({
                vacancy_id: safeVacancyId || null,
                candidate_id: candidateId,
                score: newReviewScore,
                feedback: newReviewText,
                reviewer_id: userData?.user?.id,
                reviewer_name: reviewerName,
                reviewer_email: userData?.user?.email || 'unknown@example.com'
            });
            
            if (error) throw error;
            
            setNewReviewScore(0);
            setNewReviewText('');
            loadData(); // reload reviews
        } catch (e: any) {
            console.error(e);
            alert("Fout bij opslaan review: " + e.message);
        } finally {
            setIsSubmittingReview(false);
        }
    };

    // Helper wrapper because the imported generateWordDocument function name
    const generateWordCVBytes = (buffer: ArrayBuffer, data: any) => {
        return generateWordDocument(buffer, data);
    }

    const handleSaveRawCv = async () => {
        if (blockPortalMutation()) return;
        setIsSavingRawCv(true);
        try {
            const updatedAiData = {
                ...(c.ai_parsed_data || {}),
                raw_cv_text: editRawCv
            };
            const { error } = await supabase.from('candidates')
                .update({ ai_parsed_data: updatedAiData })
                .eq('id', c.id);
            if (error) throw error;
            setIsEditingRawCv(false);
            setProfile({ ...profile, ai_parsed_data: updatedAiData });
            alert("Ruwe CV data opgeslagen!");
        } catch (e: any) {
            console.error("Save error", e);
            alert("Opslaan mislukt: " + e.message);
        } finally {
            setIsSavingRawCv(false);
        }
    };

    const loadData = async () => {
        setIsLoading(true);
        try {
            // 1. Start independent queries immediately
            const appDataPromise = safeVacancyId ? supabase.from('applications').select('status').eq('vacancy_id', safeVacancyId).eq('candidate_id', candidateId).maybeSingle() : Promise.resolve({ data: null });
            
            const reviewsPromise = safeVacancyId 
                ? supabase_get_candidate_reviews(safeVacancyId, candidateId)
                : supabase.from('candidate_reviews').select('*').eq('candidate_id', candidateId).order('created_at', { ascending: false });

            const interviewsPromise = safeVacancyId
                ? supabase.from('interviews').select('*').eq('candidate_id', candidateId).eq('vacancy_id', safeVacancyId).order('scheduled_at', { ascending: false })
                : supabase.from('interviews').select('*').eq('candidate_id', candidateId).order('scheduled_at', { ascending: false });

            const appsPromise = supabase.from('applications').select('*, vacancies(title)').eq('candidate_id', candidateId).order('created_at', { ascending: false });

            const vacQuery = supabase.from('vacancies').select('id, title, status, data, vacancy_number, clients!vacancies_client_id_fkey(name)').order('created_at', { ascending: false });
            if (clientId && clientId !== 'system') vacQuery.eq('client_id', clientId);
            const vacsPromise = vacQuery;

            // 2. Await the primary profile
            const p = await supabase_get_candidate_profile_by_id(candidateId, clientId);
            console.log("CandidateDetailModal profile loaded:", p);
            setProfile(p);
            
            if (p) {
                window.dispatchEvent(new CustomEvent('wt:taskbar-add', { 
                    detail: { 
                        id: candidateId, 
                        type: 'candidate', 
                        title: p.candidate?.name || p.name || 'Kandidaat' 
                    } 
                }));
            }

            // 3. Kick off dependent queries now that we have p
            let docsPromise = Promise.resolve([]);
            let commsPromise = Promise.resolve([]);

            if (p) {
                const globalId = p.candidate_id || p.candidate?.id || (p as any).candidates?.id || candidateId;
                if (clientId && clientId !== 'system') {
                    docsPromise = supabase_get_documents(clientId, false, globalId) as any;
                }
                const realId = p.real_profile_id;
                if (realId && !(typeof p.id === 'string' && p.id.startsWith('virtual-profile-'))) {
                    commsPromise = supabase_get_candidate_communications(realId) as any;
                }
            }

            // 4. Await everything together
            const [
                { data: appData },
                reviewsData,
                { data: ivs },
                { data: apps },
                { data: vacs },
                docs,
                comms
            ] = await Promise.all([
                appDataPromise,
                reviewsPromise,
                interviewsPromise,
                appsPromise,
                vacsPromise,
                docsPromise,
                commsPromise
            ]);

            // 5. Process and set all states synchronously
            if (p && p.status) setCurrentStageId(p.status);
            if (appData) setCurrentStageId(appData.status);

            if (p) {
                const c = p.candidate || (p as any).candidates || {};
                setEditPhone(c.phone || '');
                setEditEmail(c.email || '');
                setEditSourceInput(p.ai_parsed_data?.source || '');
                setEditAvailableFrom(p.ai_parsed_data?.available_from?.split('T')[0] || c.available_from?.split('T')[0] || '');
                
                let pref = p.ai_parsed_data?.employment_preference || c.employment_preference || '';
                if (Array.isArray(pref)) pref = pref.join(', ');
                setEditEmploymentPreference(pref);
            }

            setDocuments(docs || []);
            setCommunications(comms || []);

            // For reviews, supabase_get_candidate_reviews returns data directly, while the fallback query returns { data }
            setReviews((safeVacancyId ? reviewsData : (reviewsData as any).data) || []);
            if (ivs) setInterviews(ivs);
            if (apps) setApplications(apps);

            if (vacs) setAllVacancies(vacs.filter((v: any) => {
                const s = (v.status || v.data?.lifecycleStatus || v.data?.status || '').toLowerCase();
                return s.includes('open') || s.includes('doing') || s.includes('accepting');
            }));

            if (p) {
                let parsed: any = {};
                const sources = [(p as any).parsed_cv, p.ai_parsed_data, p.candidate?.parsed_cv, p.candidate?.ai_parsed_data];
                sources.forEach(s => { 
                    if (s && typeof s === 'object') parsed = { ...parsed, ...s }; 
                    else if (typeof s === 'string') { try { parsed = { ...parsed, ...JSON.parse(s) }; } catch(e){} } 
                });
                if (parsed.bullhornData) {
                    parsed = { ...parsed.bullhornData, ...parsed };
                }
                
                let fallbackSummary = '';
                if (!parsed.summary && !parsed.profiel && p.candidate?.original_cv_text) {
                    fallbackSummary = p.candidate.original_cv_text.replace(/<[^>]*>?/gm, '').trim();
                }
                const summaryText = parsed.summary || parsed.profiel || parsed.description || fallbackSummary || '';
                setEditRawCv(summaryText);
            }

        } catch (e) {
            console.error("Error loading candidate details", e);
        } finally {
            setIsLoading(false);
        }
    };

    const handleLinkVacancy = async (selectedVacId: string) => {
        if (blockPortalMutation()) return;
        setIsLinkingVacancy(true);
        try {
            // Check if application already exists
            const existing = applications.find(a => a.vacancy_id === selectedVacId);
            if (existing) {
                alert("Deze kandidaat is al gekoppeld aan deze vacature.");
                return;
            }

            const { data, error } = await supabase.from('applications').insert({
                candidate_id: candidateId,
                vacancy_id: selectedVacId,
                status: 'Shortlist',
            }).select('*, vacancies(title)').single();

            if (error) throw error;
            if (data) {
                setApplications([data, ...applications]);
                alert("Kandidaat succesvol gekoppeld aan vacature!");
                setShowVacancySelector(false);
            }
        } catch (e: any) {
            console.error("Link error", e);
            alert("Fout bij koppelen vacature: " + e.message);
        } finally {
            setIsLinkingVacancy(false);
        }
    };

    const reloadCommunications = async () => {
        if (!profile?.real_profile_id || (typeof profile?.id === 'string' && profile.id.startsWith('virtual-profile-'))) return;
        try {
            const comms = await supabase_get_candidate_communications(profile.real_profile_id);
            setCommunications(comms || []);
            scrollToBottom();
        } catch (e) {
            console.error("Error reloading communications", e);
        }
    };

    const handleStageChange = async (newStageId: string) => {
        if (blockPortalMutation()) return;
        if (!vacancyId || !profile) return;
        setIsUpdatingStage(true);
        try {
            // We need the application ID. For now assuming we can update by candidate+vacancy
            // Or we use the reusable service which takes AppID usually, but let's do a direct update map
            if (!safeVacancyId) { alert('Geen specifieke vacature gekoppeld, fase niet wijzigbaar.'); return; }
            const { error } = await supabase.from('applications')
                .update({ status: newStageId })
                .eq('vacancy_id', safeVacancyId)
                .eq('candidate_id', candidateId);

            if (error) throw error;

            setCurrentStageId(newStageId);
            if (onUpdate) onUpdate(); // Refresh board
        } catch (e) {
            console.error("Failed to update stage", e);
            alert("Kon fase niet wijzigen.");
        } finally {
            setIsUpdatingStage(false);
        }
    };

    const handleAddTodo = async () => {
        if (blockPortalMutation()) return;
        if (!newTodo.trim()) return;
        setIsSavingTodo(true);
        try {
            // Using the same 'todos' table as the widget
            await supabase.from('todos').insert({
                title: newTodo,
                user_id: user?.id || 'unknown',
                vacancy_id: vacancyId || null,
                candidate_id: candidateId,
                status: 'Pending',
                priority: 'medium',
                assignee_id: user?.id, // Assign to self by default
                quick_link_url: window.location.pathname + window.location.search,
                quick_link_title: `Kandidaat: ${profile?.candidate?.name || profile?.candidates?.name || 'Onbekend'}`
            });
            setNewTodo('');
            setShowTodoInput(false);
            alert("Taak toegevoegd!");
        } catch (e) {
            console.error("Failed to add todo", e);
        } finally {
            setIsSavingTodo(false);
        }
    };

    const handleSendMessage = async () => {
        if (blockPortalMutation()) return;
        if (!newMessage.trim() || !profile || !profile.real_profile_id) return;
        setIsSending(true);
        try {
            const res = await supabase_save_candidate_communication(profile.real_profile_id, user?.id || 'unknown', newMessage, 'note');
            if (res) {
                setCommunications([...communications, res]);
                setNewMessage('');
            }
        } catch (e) {
            console.error("Failed to send", e);
        } finally {
            setIsSending(false);
        }
    };

    const handleSendWhatsAppDraft = async (comm: any) => {
        if (blockPortalMutation()) return;
        if (!c.phone) {
            alert("Kandidaat heeft geen telefoonnummer.");
            return;
        }

        const message = comm.content?.split('---RAW_TRANSCRIPT---')[0];
        const encodedMessage = encodeURIComponent(message);

        let phone = c.phone.replace(/[^0-9+]/g, '');
        if (phone.startsWith('0')) {
            phone = '+31' + phone.substring(1);
        }

        const url = `https://wa.me/${phone}?text=${encodedMessage}`;
        window.open(url, '_blank');

        // Update draft to sent note
        if (comm.id) {
            await supabase.from('candidate_communications').update({ type: 'whatsapp (verzonden via web)', content: `✅ WhatsApp Concept Geopend:\n\n${comm.content}` }).eq('id', comm.id);
            reloadCommunications();
        }
    };

    const handleInitiateCall = async () => {
        if (blockPortalMutation()) return;
        // Now handled by CallSidebar
        setEditCommId(undefined);
        setEditCommContent(undefined);
        setShowCallModal(true);
    };

    const handleEditCallNote = (comm: any) => {
        if (blockPortalMutation()) return;
        if (comm.type !== 'call') return;
        setEditCommId(comm.id);
        setEditCommContent(comm.content);
        setShowCallModal(true);
    };

    const saveInboundWhatsAppPaste = async () => {
        if (blockPortalMutation()) return;
        const pid = profile?.real_profile_id;
        if (!inboundWaPaste.trim() || !pid) return;
        setSavingInboundWa(true);
        try {
            const res = await supabase_save_candidate_communication(
                pid,
                user?.id || 'unknown',
                `📩 Inkomend WhatsApp (geplakt uit WhatsApp Web of de app):\n\n${inboundWaPaste.trim()}`,
                'whatsapp_inbound'
            );
            if (res) {
                setCommunications((prev) => [...prev, res]);
                setInboundWaPaste('');
            }
        } catch (e) {
            console.error(e);
        } finally {
            setSavingInboundWa(false);
        }
    };

    const saveInboundEmailPaste = async () => {
        if (blockPortalMutation()) return;
        const pid = profile?.real_profile_id;
        if (!inboundEmailPaste.trim() || !pid) return;
        setSavingInboundEmail(true);
        try {
            const res = await supabase_save_candidate_communication(
                pid,
                user?.id || 'unknown',
                `📩 Inkomende e-mail (handmatig geplakt):\n\n${inboundEmailPaste.trim()}`,
                'email_inbound'
            );
            if (res) {
                setCommunications((prev) => [...prev, res]);
                setInboundEmailPaste('');
            }
        } catch (e) {
            console.error(e);
        } finally {
            setSavingInboundEmail(false);
        }
    };

    const handleSaveContactInfo = async () => {
        if (blockPortalMutation()) return;
        const c = profile?.candidate || profile?.candidates;
        if (!c) return;
        setIsSavingContact(true);
        try {
            const updated = await supabase_update_candidate(c.id, {
                phone: editPhone,
                email: editEmail
            });

            const iso = (editAvailableFrom || '').trim();
            const nl = iso ? new Date(`${iso}T12:00:00`).toLocaleDateString('nl-NL') : null;
            const updatedAiData: Record<string, any> = {
                ...(c.ai_parsed_data || {}),
                source: editSourceInput,
                employment_preference: editEmploymentPreference
            };
            if (iso) {
                updatedAiData.available_from = iso;
            } else {
                delete updatedAiData.available_from;
            }

            await supabase
                .from('candidates')
                .update({
                    ai_parsed_data: updatedAiData,
                    availability: nl,
                })
                .eq('id', c.id);

            if (updated) {
                setProfile({
                    ...profile,
                    ai_parsed_data: updatedAiData,
                    candidate: {
                        ...c,
                        phone: editPhone,
                        email: editEmail,
                        availability: nl,
                        ai_parsed_data: updatedAiData,
                    },
                });
                setIsEditingContact(false);
                if (onUpdate) onUpdate();
                alert('Gegevens opgeslagen!');
            }
        } catch (e) {
            console.error('Failed to save contact info', e);
            alert('Opslaan mislukt.');
        } finally {
            setIsSavingContact(false);
        }
    };

    const handleSaveMotivation = async () => {
        if (blockPortalMutation()) return;
        const cand = profile?.candidate || profile?.candidates;
        if (!cand?.id) return;
        setSavingMotivation(true);
        try {
            const updatedAi = { ...(cand.ai_parsed_data || {}), motivation: motivationDraft.trim() };
            const { error } = await supabase.from('candidates').update({ ai_parsed_data: updatedAi }).eq('id', cand.id);
            if (error) throw error;
            setProfile({
                ...profile,
                ai_parsed_data: updatedAi,
                candidate: { ...cand, ai_parsed_data: updatedAi },
            });
            setEditingMotivation(false);
            onUpdate?.();
            alert('Motivatie opgeslagen');
        } catch (e) {
            console.error(e);
            alert('Opslaan mislukt.');
        } finally {
            setSavingMotivation(false);
        }
    };

    const handleDeleteMotivation = async () => {
        if (blockPortalMutation()) return;
        if (!confirm('Motivatie verwijderen?')) return;
        const cand = profile?.candidate || profile?.candidates;
        if (!cand?.id) return;
        setSavingMotivation(true);
        try {
            const updatedAi = { ...(cand.ai_parsed_data || {}) };
            delete updatedAi.motivation;
            const { error } = await supabase.from('candidates').update({ ai_parsed_data: updatedAi }).eq('id', cand.id);
            if (error) throw error;
            setMotivationDraft('');
            setProfile({
                ...profile,
                ai_parsed_data: updatedAi,
                candidate: { ...cand, ai_parsed_data: updatedAi },
            });
            setEditingMotivation(false);
            onUpdate?.();
        } catch (e) {
            console.error(e);
            alert('Verwijderen mislukt.');
        } finally {
            setSavingMotivation(false);
        }
    };

    const handleSaveWorkDraft = async () => {
        if (blockPortalMutation()) return;
        const cand = profile?.candidate || profile?.candidates;
        if (!cand?.id) return;
        setSavingWork(true);
        try {
            const updatedAi = { ...(cand.ai_parsed_data || {}), timeline: workDraft };
            const { error } = await supabase.from('candidates').update({ ai_parsed_data: updatedAi }).eq('id', cand.id);
            if (error) throw error;
            setProfile({
                ...profile,
                ai_parsed_data: updatedAi,
                candidate: { ...cand, ai_parsed_data: updatedAi },
            });
            setEditingWork(false);
            onUpdate?.();
            alert('Werkervaring opgeslagen');
        } catch (e) {
            console.error(e);
            alert('Opslaan mislukt.');
        } finally {
            setSavingWork(false);
        }
    };

    const handleSaveEduDraft = async () => {
        if (blockPortalMutation()) return;
        const cand = profile?.candidate || profile?.candidates;
        if (!cand?.id) return;
        setSavingEdu(true);
        try {
            const updatedAi = { ...(cand.ai_parsed_data || {}), education: eduDraft };
            const { error } = await supabase.from('candidates').update({ ai_parsed_data: updatedAi }).eq('id', cand.id);
            if (error) throw error;
            setProfile({
                ...profile,
                ai_parsed_data: updatedAi,
                candidate: { ...cand, ai_parsed_data: updatedAi },
            });
            setEditingEdu(false);
            onUpdate?.();
            alert('Opleidingen opgeslagen');
        } catch (e) {
            console.error(e);
            alert('Opslaan mislukt.');
        } finally {
            setSavingEdu(false);
        }
    };

    const handleGenerateMail = async () => {
        if (blockPortalMutation()) return;
        if (!mailPrompt.trim()) return;
        setIsGeneratingMail(true);
        setGeneratedMail('');
        try {
            const prompt = `Schrijf een wervende, professionele e-mail naar kandidaat "${c.name || 'de kandidaat'}". 
Context / Instructie van recruiter: ${mailPrompt}
Zorg voor een vlotte, moderne toon. Gebruik geen placeholders zoals [Naam Bedrijf] tenzij onvermijdelijk (probeer het algemeen te houden of gebruik de info uit de instructie). Sluit af met een professionele groet.`;
            const result = await generateTemplateContent(prompt, "email"); // Changed performRealMatch to generateTemplateContent
            
            // Clean up Gemini markdown garbage if any
            let clean = result;
            if (clean.includes('```')) {
                clean = clean.replace(/```(email)?/gi, '').trim();
            }
            if (clean.includes('**Onderwerp:**')) {
                clean = clean.replace('**Onderwerp:**', 'Onderwerp:');
            }
            
            setGeneratedMail(clean);
        } catch (e) {
            console.error(e);
            alert("Genereren mislukt. Probeer het opnieuw.");
        } finally {
            setIsGeneratingMail(false);
        }
    };

    const handleOpenInM365 = () => {
        if (blockPortalMutation()) return;
        if (!c.email) return;
        // Parse subject and body
        let subject = "Bericht van ons";
        let body = generatedMail;
        
        if (generatedMail.toLowerCase().includes('onderwerp:')) {
            const parts = generatedMail.split(/onderwerp:/i);
            if (parts.length > 1) {
                const subSplit = parts[1].split('\n');
                subject = subSplit[0].trim();
                body = subSplit.slice(1).join('\n').trim();
            }
        }
        
        const mailtoUrl = `mailto:${c.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
        window.location.href = mailtoUrl;
        setShowMailBuilder(false);
    };

    const handleSaveTags = async () => {
        if (blockPortalMutation()) return;
        if (!profile) return;
        setIsSavingTags(true);
        try {
            // Parse comma-separated input into array
            const newTagsArray = editTagsInput.split(',').map(t => t.trim()).filter(Boolean);
            
            // We save it to ai_parsed_data.tags and also candidate tags
            const updatedAiData = {
                ...(c.ai_parsed_data || {}),
                tags: newTagsArray,
                skills: newTagsArray // Keep both for safety
            };

            const candidateData = profile.candidate || profile.candidates || {};

            const { error: pErr } = await supabase.from('candidates')
                .update({ ai_parsed_data: updatedAiData })
                .eq('id', c.id);

            if (pErr) throw pErr;

            // Update candidate
            await supabase.from('candidates')
                .update({ tags: newTagsArray })
                .eq('id', candidateData.id);

            // Update local UI
            setProfile({
                ...profile,
                ai_parsed_data: updatedAiData,
                candidate: { ...candidateData, tags: newTagsArray }
            });
            setIsEditingTags(false);
            if (onUpdate) onUpdate(); // Refresh board
            alert('Tags succesvol opgeslagen!');
        } catch (e) {
            console.error('Save tags failed:', e);
            alert('Tags opslaan mislukt.');
        } finally {
            setIsSavingTags(false);
        }
    };

    const handleManualAiAnalysis = async () => {
        if (blockPortalMutation()) return;
        if (!profile) return;
        setIsAnalyzing(true);
        try {
            const c = profile.candidate || profile.candidates || {};

            let targetApps = applications;
            if (safeVacancyId) {
                targetApps = applications.filter((a: any) => a.vacancy_id === safeVacancyId);
            }

            if (targetApps.length === 0) {
                alert("Kandidaat is niet gekoppeld aan vacatures om mee te matchen.");
                return;
            }

            let analyzedCount = 0;
            for (const app of targetApps) {
                let vacData = app.vacancies;
                if (!vacData || !vacData.description) {
                    const { data } = await supabase.from('vacancies').select('title, description').eq('id', app.vacancy_id).single();
                    if (data) vacData = data;
                }
                if (!vacData) continue;

                let safeTags = [];
                if (Array.isArray(c.tags)) {
                    safeTags = c.tags.map((t: any) => typeof t === 'object' ? t.name : t).filter(Boolean);
                }

                const analysisData = {
                    name: c.name || '',
                    role: c.role || '',
                    summary: profile.parsed_cv?.summary || parsed.summary || '',
                    skills: profile.parsed_cv?.skills || profile.parsed_cv?.tags || parsed.tags || safeTags || [],
                    timeline: profile.parsed_cv?.timeline || parsed.timeline || []
                };

                const vacContext = {
                    title: vacData.title,
                    description: vacData.description,
                    data: {}
                };

                const matchResult = await performRealMatch(analysisData, vacContext);

                if (matchResult && typeof matchResult.score !== 'undefined') {
                    // Update candidate profile globally
                    const updatedAiData = {
                        ...(c.ai_parsed_data || {}),
                        match_score: matchResult.score,
                        match_reason: matchResult.reason || '',
                        strong_points: matchResult.pros || [],
                        weak_points: matchResult.cons || []
                    };
        
                    await supabase.from('candidates')
                        .update({
                            ai_parsed_data: updatedAiData
                        })
                        .eq('id', c.id);
        
                    // Update specific application
                    await supabase.from('applications')
                        .update({
                            match_score: matchResult.score,
                            cv_summary: `AI Match (${matchResult.score}%): ${matchResult.reason || ''}`
                        })
                        .eq('vacancy_id', app.vacancy_id)
                        .eq('id', candidateId);
                        
                    analyzedCount++;
                }
            }

            if (analyzedCount > 0) {
                alert(`AI Analyse voltooid voor ${analyzedCount} vacature(s)!`);
                // Try calling loadData if it exists in scope, assuming it was called originally
                if (typeof loadData === 'function') {
                    loadData(); // reload Everything to reflect new score
                }
            } else {
                alert("Kon voor geen van de geselecteerde vacatures een match berekenen.");
            }
        } catch (e: any) {
            console.error("AI Analysis failed", e);
            alert("Fout bij AI analyse: " + (e.message || String(e)));
        } finally {
            setIsAnalyzing(false);
        }
    };

    if (isLoading) {
        return (
            <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm animate-fadeIn">
                <div className="bg-white p-8 rounded-2xl shadow-2xl flex flex-col items-center gap-4 animate-scaleIn">
                    <Loader2 className="animate-spin text-red-" size={48} />
                </div>
            </div>
        );
    }

    if (!profile || (!profile.candidate && !profile.candidates)) return null;

    let c = profile.candidate || profile.candidates || {};
    
    const portalApplicationForMask =
        safeVacancyId && applications?.length
            ? applications.find(
                  (a: any) =>
                      String(a.vacancy_id) === String(safeVacancyId) && String(a.candidate_id) === String(candidateId),
              )
            : undefined;
    const activeApplicationId = portalApplicationForMask?.id as string | undefined;
    const shouldMaskIdentity =
        (isClientPortalHost || isClientPortalUser) &&
        isPortalHideCandidateNameForStatus(String(currentStageId || c.status || ''), portalApplicationForMask, portalPrivacy);

    if (shouldMaskIdentity) {
        c = {
            ...c,
            name: 'Kandidaat (vertrouwelijk)',
            email: 'verborgen',
            phone: 'verborgen',
            photo_url: null,
            avatar_url: null,
            linkedin_url: null,
            contact_email: null,
            mobile: null
        };
    }

    // Safely extract and merge parsed data from all possible locations
    let parsed: any = {};
    const sources = [profile.parsed_cv, profile.ai_parsed_data, c.parsed_cv, c.ai_parsed_data];
    sources.forEach(source => {
        if (source) {
            try {
                let data = source; while (typeof data === 'string') { try { data = JSON.parse(data); } catch(e) { break; } }
                if (typeof data === 'object' && Object.keys(data).length > 0) {
                    parsed = { ...parsed, ...data };
                }
            } catch (e) { /* ignore parse error */ }
        }
    });

    if (parsed.bullhornData) {
        parsed = { ...parsed.bullhornData, ...parsed }; // Surface Bullhorn sync data properly
    }

    const timeline = parsed.timeline || parsed.workHistory || parsed.workHistories || parsed.work_experience || [];
    const education = parsed.education || parsed.educations || [];
    
    // Safely parse arrays that might be strings containing JSON or comma-separated
    let safeSkills: string[] = [];
    const rawSkills = parsed.tags || parsed.skills || parsed.skillSet || c.skill_set || [];
    if (Array.isArray(rawSkills)) {
        safeSkills = rawSkills;
    } else if (typeof rawSkills === 'string') {
        try { safeSkills = JSON.parse(rawSkills); } catch(e) { safeSkills = rawSkills.split(',').map(s => s.trim()); }
    }
    const skills = safeSkills;

    // Fallback logic for summary: checks parsed fields, then description, then strips HTML from original_cv_text
    let fallbackSummary = '';
    if (!parsed.summary && !parsed.profiel && c.original_cv_text) {
        let cleanText = c.original_cv_text.replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '');
        cleanText = cleanText.replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '');
        fallbackSummary = cleanText.replace(/<[^>]*>?/gm, '').trim();
    }
    const summary = parsed.summary || parsed.profiel || parsed.description || fallbackSummary || '';
    
    let rawCvDisplay = parsed.raw_cv_text;
    if (!rawCvDisplay && c.original_cv_text) {
        let cleanText = c.original_cv_text.replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '').replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '');
        rawCvDisplay = cleanText.replace(/<[^>]*>?/gm, '').trim();
    }
    if (!rawCvDisplay) {
        rawCvDisplay = parsed.motivation || parsed.description || parsed.summary || parsed.profiel || '';
    }
    
    const matchReason = parsed.match_reason || parsed.match_analyse || "Beschikbare data wordt nog verwerkt.";
    const strongPoints = parsed.strong_points || parsed.pluspunten || [];
    const weakPoints = parsed.weak_points || parsed.aandachtspunten || [];
    const matchScore = parsed.match_score || parsed.match_score || c.match_score || 0;

    return createPortal(
        <div className="fixed inset-0 z-[120] flex justify-end bg-slate-900/40 animate-fadeIn">
            <div 
                className={`bg-white h-full shadow-2xl flex flex-col overflow-hidden animate-slideInRight ring-1 ring-slate-900/5 transition-all duration-300 ${showCallModal ? 'w-[calc(100vw-320px)] lg:max-w-[calc(1152px-320px)] mr-[320px]' : 'w-full sm:w-[95vw] md:w-[90vw] lg:max-w-6xl'}`}
            >

                {/* COMPACT HEADER */}
                <div className="px-6 py-4 border-b border-slate-100 bg-white z-10 shrink-0 flex items-center justify-between gap-4">
                    <div className="flex items-center gap-4 min-w-0">
                        <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center text-lg font-black text-slate-400 shrink-0 overflow-hidden border border-slate-200">
                            {c.photo_url ? (
                                <img src={c.photo_url} alt={c.name} className="w-full h-full object-cover" />
                            ) : (
                                c.name.charAt(0)
                            )}
                        </div>
                        <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-3">
                                <h2 className="text-lg font-black text-slate-900 tracking-tight truncate leading-tight">{c.name}</h2>
                                {c.candidate_number && (
                                    <span className="text-[10px] font-black text-slate-400 bg-slate-100 px-2 py-0.5 rounded-md border border-slate-200 uppercase tracking-widest shrink-0">
                                        #{c.candidate_number}
                                    </span>
                                )}
                                {(() => {
                                    let displayScore = matchScore;
                                    if (vacancyId) {
                                        const activeApp = applications.find(a => a.vacancy_id === vacancyId);
                                        if (activeApp && activeApp.match_score) displayScore = activeApp.match_score;
                                    }
                                    if (displayScore > 0) {
                                        return (
                                            <span className={`px-2 py-0.5 rounded-md border text-[10px] font-black uppercase tracking-widest flex items-center gap-1.5 shrink-0 shadow-sm ${displayScore > 70 ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : displayScore > 40 ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-red-50 text-red-700 border-red-200'}`}>
                                                Match {displayScore}%
                                            </span>
                                        );
                                    }
                                    return null;
                                })()}
                                {c.status && (
                                    <span className="px-2 py-0.5 rounded-md bg-slate-100 border border-slate-200 text-[10px] font-bold text-slate-600 uppercase tracking-widest flex items-center gap-1.5 shrink-0">
                                        <div className={`w-1.5 h-1.5 rounded-full ${c.status === 'Active' ? 'bg-emerald-500' : 'bg-slate-400'}`}></div>
                                        {c.status}
                                    </span>
                                )}
                                {(c.linkedin_url || parsed.linkedin || parsed.socials?.linkedin) && (
                                    <a
                                        href={c.linkedin_url || parsed.linkedin || parsed.socials?.linkedin}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-[#0A66C2] hover:bg-[#0A66C2]/10 p-1.5 rounded-md transition-colors shrink-0"
                                        title="Bekijk op LinkedIn"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                            <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
                                        </svg>
                                    </a>
                                )}
                                <button
                                    onClick={(e) => {
                                        e.preventDefault();
                                        const slug = generateCandidateSlug(c.name || '', c.candidate_number || c.id);
                                        window.history.pushState({ tab: 'talentpool' }, '', '/app/talentpool?candidate=' + slug);
                                        window.dispatchEvent(new CustomEvent('open-candidate', { detail: candidateId }));
                                        onClose();
                                    }}
                                    className="text-indigo-600 hover:bg-indigo-50 p-1.5 rounded-md transition-colors shrink-0 cursor-pointer border-none bg-transparent flex items-center justify-center outline-none"
                                    title="Open Volledig Talentpool Profiel"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-external-link"><path d="M15 3h6v6"/><path d="M10 14 21 3"/><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/></svg>
                                </button>
                            </div>
                            <p className="text-xs text-slate-500 font-medium truncate flex items-center gap-2 mt-0.5">
                                {c.role || parsed.role || 'Kandidaat'}
                                {c.city && <span className="hidden sm:inline opacity-50">• {c.city}</span>}
                            </p>
                        </div>
                    </div>

                    <div className="flex items-center gap-3 shrink-0">


                        {/* Quick Actions */}
                        <div className="relative">
                            <button
                                type="button"
                                onClick={() => {
                                    if (isReadOnlyPortal) {
                                        void blockPortalMutation();
                                        return;
                                    }
                                    setShowTodoInput(!showTodoInput);
                                }}
                                disabled={isReadOnlyPortal}
                                className={`p-2 rounded-lg transition-colors relative ${showTodoInput ? 'bg-red- text-red-' : 'text-slate-400 hover:bg-slate-100 hover:text-slate-600'} ${isReadOnlyPortal ? 'cursor-not-allowed opacity-40' : ''}`}
                                title="Taak toevoegen"
                            >
                                <CheckSquare size={18} />
                            </button>
                            {/* Popover Todo Input */}
                            {showTodoInput && (
                                <div className="absolute top-full right-0 mt-2 w-64 bg-white rounded-xl shadow-xl border border-slate-100 p-3 z-50 animate-fadeIn">
                                    <input
                                        autoFocus
                                        value={newTodo}
                                        onChange={e => setNewTodo(e.target.value)}
                                        placeholder="Wat moet er gebeuren?"
                                        className="w-full text-xs font-medium bg-slate-50 border border-slate-200 rounded-lg p-2 mb-2 outline-none focus:border-red-"
                                        onKeyDown={e => e.key === 'Enter' && handleAddTodo()}
                                    />
                                    <div className="flex justify-end gap-2">
                                        <button onClick={() => setShowTodoInput(false)} className="text-[10px] font-bold text-slate-400 hover:text-slate-600 px-2 py-1">Annuleren</button>
                                        <button onClick={handleAddTodo} disabled={!newTodo.trim() || isSavingTodo} className="bg-slate-900 text-white text-[10px] font-bold px-3 py-1 rounded-md hover:bg-black">Toevoegen</button>
                                    </div>
                                </div>
                            )}
                        </div>

                        {(() => {
                            const candidateEmail = c.email || c.contact_email || parsed.email || parsed.contact_info?.email || '';
                            const rawPhone = c.phone || c.mobile || parsed.phone || parsed.contact_info?.phone || '';
                            const candidatePhone = rawPhone.replace(/[^0-9+]/g, '');
                            
                            return (
                                <>
                                    {candidateEmail && (
                                        <button onClick={() => isReadOnlyPortal ? window.dispatchEvent(new CustomEvent('wt:toast', { detail: { message: 'Upgrade portal voor mail en whatsapp, contact Werfplaats.', type: 'error' } })) : setShowMailBuilder(true)} className={`p-2 rounded-lg transition-colors cursor-pointer ${isReadOnlyPortal ? 'text-slate-300 cursor-not-allowed opacity-50 blur-[1px]' : 'text-slate-400 hover:bg-slate-100 hover:text-red-500'}`} title={isReadOnlyPortal ? "Upgrade portal voor mail en whatsapp" : `Stuur e-mail naar ${candidateEmail}`}>
                                            <Mail size={18} />
                                        </button>
                                    )}
                                    {candidatePhone && (
                                        <button onClick={() => isReadOnlyPortal ? window.dispatchEvent(new CustomEvent('wt:toast', { detail: { message: 'Upgrade portal voor mail en whatsapp, contact Werfplaats.', type: 'error' } })) : setShowWhatsAppBuilder(true)} className={`p-2 rounded-lg transition-colors cursor-pointer ${isReadOnlyPortal ? 'text-slate-300 cursor-not-allowed opacity-50 blur-[1px]' : 'text-slate-400 hover:bg-green-50 hover:text-green-600'}`} title={isReadOnlyPortal ? "Upgrade portal voor mail en whatsapp" : `WhatsApp naar ${rawPhone}`}>
                                            <MessageSquare size={18} />
                                        </button>
                                    )}
                                    {candidatePhone && (
                                        <button onClick={() => { if (blockPortalMutation()) return; setShowCallModal(true); }} className="p-2 text-slate-400 hover:bg-emerald-50 hover:text-emerald-600 rounded-lg transition-colors cursor-pointer" title={`Log Call`}>
                                            <Phone size={18} />
                                        </button>
                                    )}
                                </>
                            );
                        })()}
                        <button onClick={() => { if (blockPortalMutation()) return; if (onScheduleClick) onScheduleClick(); else setShowTeamsScheduler(true); }} className="p-2 text-slate-400 hover:bg-slate-100 hover:text-red-500 rounded-lg transition-colors" title="Interview Plannen">
                            <Calendar size={18} />
                        </button>
                        {(c.linkedin_url || profile?.linkedin_url || parsed.linkedin || parsed.socials?.linkedin) && (
                            <a href={c.linkedin_url || profile?.linkedin_url || parsed.linkedin || parsed.socials?.linkedin} target="_blank" rel="noreferrer" className="p-2 text-slate-400 hover:bg-slate-100 hover:text-[#0A66C2] rounded-lg transition-colors" title="LinkedIn">
                                <Linkedin size={18} />
                            </a>
                        )}

                        {!isReadOnlyPortal && parsed.id && user?.bullhorn_settings?.restUrl && (
                            <button
                                onClick={handleDeepSync}
                                disabled={isDeepSyncing}
                                className="p-2 text-slate-400 hover:bg-indigo-50 hover:text-indigo-600 rounded-lg transition-colors"
                                title="Deep Sync from Bullhorn (Notities, CV data, Documenten ophalen)"
                            >
                                {isDeepSyncing ? <Loader2 size={18} className="animate-spin text-indigo-600" /> : <Cloud size={18} />}
                            </button>
                        )}

                        <button onClick={onClose} className="p-2 hover:bg-red-50 hover:text-red-500 rounded-full transition-colors ml-2">
                            <X size={20} />
                        </button>
                    </div>
                </div>

                {/* Main Content Layout */}
                <div className="flex-1 flex overflow-hidden">
                    {/* Sidebar / Tabs (Desktop) */}
                    <div className="w-56 bg-slate-50 border-r border-slate-100 p-4 flex flex-col gap-1 shrink-0 hidden md:flex">
                        <NavButton icon={<User size={16} />} label="Overzicht" active={activeTab === 'overview'} onClick={() => setActiveTab('overview')} />
                        <NavButton icon={<FileText size={16} />} label="Documenten" count={documents.length} active={activeTab === 'documents'} onClick={() => setActiveTab('documents')} />
                        {!portalLimitedTabs ? (
                            <>
                                <NavButton icon={<StickyNote size={16} />} label="Notities" count={communications.filter(c => c.type === 'note').length} active={activeTab === 'notes'} onClick={() => setActiveTab('notes')} />
                                <NavButton icon={<Activity size={16} />} label="Activiteit" count={communications.filter(c => c.type === 'system').length} active={activeTab === 'activity'} onClick={() => setActiveTab('activity')} color="text-blue-600" />
                                <NavButton icon={<MessageSquare size={16} />} label="Communicatie" count={communications.filter(c => c.type !== 'note' && c.type !== 'system').length} active={activeTab === 'comm'} onClick={() => setActiveTab('comm')} color="text-emerald-600" />
                                <NavButton icon={<Calendar size={16} />} label="Interviews" count={interviews.length} active={activeTab === 'interviews'} onClick={() => setActiveTab('interviews')} />
                                <NavButton icon={<Briefcase size={16} />} label="Vacatureflow" count={applications.length} active={activeTab === 'vacatureflow'} onClick={() => setActiveTab('vacatureflow')} color="text-[#e51b23]" />
                                <div className="h-px bg-slate-200 my-2 mx-2"></div>
                                <NavButton icon={<BrainCircuit size={16} />} label="Matching" active={activeTab === 'ai_analysis'} onClick={() => setActiveTab('ai_analysis')} color="text-purple-600" />
                                <NavButton icon={<Star size={16} />} label="Reviews" count={reviews.length} active={activeTab === 'reviews'} onClick={() => setActiveTab('reviews')} color="text-amber-600" />
                            </>
                        ) : null}
                    </div>

                    {/* Content Area */}
                    <div className="flex-1 overflow-y-auto custom-scrollbar p-6 bg-white relative">
                        {/* Mobile Tabs */}
                        <div className="md:hidden flex gap-2 mb-6 overflow-x-auto pb-2 scrollbar-none">
                            {(portalLimitedTabs
                                ? ['overview', 'documents']
                                : ['overview', 'documents', 'notes', 'activity', 'comm', 'interviews', 'vacatureflow', 'ai_analysis', 'reviews']
                            ).map((t) => {
                                let badgeCount: number | null = null;
                                if (t === 'documents') badgeCount = documents.length;
                                if (t === 'notes') badgeCount = communications.filter(comm => comm.type === 'note').length;
                                if (t === 'activity') badgeCount = communications.filter(comm => comm.type === 'system').length;
                                if (t === 'comm') badgeCount = communications.filter(comm => comm.type !== 'note' && comm.type !== 'system').length;
                                if (t === 'interviews') badgeCount = interviews.length;
                                if (t === 'vacatureflow') badgeCount = applications.length;
                                if (t === 'reviews') badgeCount = reviews.length;

                                return (
                                    <button key={t} onClick={() => setActiveTab(t as any)} className={`px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-wider whitespace-nowrap flex items-center gap-1 ${activeTab === t ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-500'}`}>
                                        {t === 'ai_analysis' ? 'Matching' : t === 'vacatureflow' ? 'Vacatures' : t === 'comm' ? 'Communicatie' : t}
                                        {badgeCount !== null && badgeCount > 0 && (
                                            <span className={`px-1.5 py-0.5 rounded-full text-[9px] ml-1 ${activeTab === t ? 'bg-white/20 text-white' : 'bg-slate-200 text-slate-600'}`}>{badgeCount}</span>
                                        )}
                                    </button>
                                );
                            })}
                        </div>

                        {activeTab === 'overview' && (
                            <div className="space-y-8 max-w-4xl animate-fadeIn mx-auto">
                                {/* Contact Info Block */}
                                <div className="bg-slate-50 p-5 rounded-xl border border-slate-100">
                                    <div className="flex justify-between items-center mb-4">
                                        <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
                                            <User size={12} /> Contactgegevens
                                        </h3>
                                        {!isReadOnlyPortal ? (
                                        <button onClick={() => setIsEditingContact(!isEditingContact)} className="text-xs font-bold text-red- hover:text-red-">
                                            {isEditingContact ? 'Annuleren' : 'Bewerken'}
                                        </button>
                                        ) : null}
                                    </div>

                                    {isEditingContact ? (
                                        <div className="flex flex-col gap-3 text-sm">
                                            <div>
                                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1">Telefoonnummer</label>
                                                <input type="text" value={editPhone} onChange={e => setEditPhone(e.target.value)} className="w-full p-2 bg-slate-50 border border-slate-200 rounded-lg outline-none focus:border-indigo-500 font-medium" />
                                            </div>
                                            <div>
                                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1">E-mailadres</label>
                                                <input type="email" value={editEmail} onChange={e => setEditEmail(e.target.value)} className="w-full p-2 bg-slate-50 border border-slate-200 rounded-lg outline-none focus:border-indigo-500 font-medium" />
                                            </div>
                                            <div>
                                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1">Bron (bijv. LinkedIn, Indeed)</label>
                                                <input type="text" value={editSourceInput} onChange={e => setEditSourceInput(e.target.value)} className="w-full p-2 bg-slate-50 border border-slate-200 rounded-lg outline-none focus:border-indigo-500 font-medium" />
                                            </div>
                                            <div>
                                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1">Beschikbaarheid (Datum)</label>
                                                <input type="date" value={editAvailableFrom} onChange={e => setEditAvailableFrom(e.target.value)} className="w-full p-2 bg-slate-50 border border-slate-200 rounded-lg outline-none focus:border-indigo-500 font-medium text-slate-700" />
                                            </div>
                                            <div>
                                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1">Voorkeur Dienstverband</label>
                                                <select value={['Vast', 'ZZP', 'Contract', 'Vast of ZZP', 'Vast of Contract'].includes(editEmploymentPreference) ? editEmploymentPreference : 'Overig'} onChange={e => {
                                                    if (e.target.value !== 'Overig') setEditEmploymentPreference(e.target.value);
                                                    else setEditEmploymentPreference('');
                                                }} className="w-full p-2 mb-2 bg-slate-50 border border-slate-200 rounded-lg outline-none focus:border-indigo-500 font-medium text-slate-700">
                                                    <option value="">-- Kies of typ zelf --</option>
                                                    <option value="Vast">Vast</option>
                                                    <option value="ZZP">ZZP</option>
                                                    <option value="Contract">Contract</option>
                                                    <option value="Vast of ZZP">Vast of ZZP</option>
                                                    <option value="Vast of Contract">Vast of Contract</option>
                                                    <option value="Overig">Anders (zelf invullen)...</option>
                                                </select>
                                                {(!['Vast', 'ZZP', 'Contract', 'Vast of ZZP', 'Vast of Contract'].includes(editEmploymentPreference) || editEmploymentPreference === '') && (
                                                    <input type="text" placeholder="Typ specifiek dienstverband..." value={editEmploymentPreference} onChange={e => setEditEmploymentPreference(e.target.value)} className="w-full p-2 bg-slate-50 border border-slate-200 rounded-lg outline-none focus:border-indigo-500 font-medium" />
                                                )}
                                            </div>
                                            <div className="flex justify-end mt-2">
                                                <button onClick={handleSaveContactInfo} disabled={isSavingContact} className="bg-[#e51b23] text-white text-xs font-bold px-4 py-2 rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors">
                                                    {isSavingContact ? 'Opslaan...' : 'Opslaan'}
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="flex flex-col gap-2">
                                            <div className="flex items-center gap-3 text-sm text-slate-700">
                                                <Phone size={14} className="text-slate-400 mt-0.5" />
                                                <span className="font-medium font-sans">{c.phone || 'Geen telefoonnummer'}</span>
                                                {c.phone && (
                                                    <div className="ml-auto flex gap-2">
                                                        <button onClick={() => isReadOnlyPortal ? window.dispatchEvent(new CustomEvent('wt:toast', { detail: { message: 'Upgrade portal voor mail en whatsapp, contact Werfplaats.', type: 'error' } })) : setShowWhatsAppBuilder(true)} className={`text-xs font-bold px-3 py-1 rounded-md transition-all shadow-sm flex items-center gap-1 ${isReadOnlyPortal ? 'bg-slate-100 text-slate-400 cursor-not-allowed opacity-50 blur-[1px]' : 'text-green-600 hover:text-white bg-green-50 hover:bg-green-500'}`}>
                                                            <MessageSquare size={12} className="fill-current" /> WhatsApp
                                                        </button>
                                                        <button onClick={() => isReadOnlyPortal ? window.dispatchEvent(new CustomEvent('wt:toast', { detail: { message: 'Deze optie is alleen voor Werfplaats recruiters.', type: 'error' } })) : setShowCallModal(true)} className={`text-xs font-bold px-3 py-1 rounded-md transition-all shadow-sm flex items-center gap-1 ${isReadOnlyPortal ? 'bg-slate-100 text-slate-400 cursor-not-allowed opacity-50 blur-[1px]' : 'text-emerald-600 hover:text-white bg-emerald-50 hover:bg-emerald-500'}`}>
                                                            <Phone size={12} className="fill-current" /> Bellen
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                            <div className="flex items-center gap-3 text-sm text-slate-700 h-7 group relative">
                                                <Mail size={14} className="text-slate-400" />
                                                <span className="font-medium">{c.email || 'Geen e-mailadres'}</span>
                                                {c.email && (
                                                    <button onClick={() => isReadOnlyPortal ? window.dispatchEvent(new CustomEvent('wt:toast', { detail: { message: 'Upgrade portal voor mail en whatsapp, contact Werfplaats.', type: 'error' } })) : setShowMailBuilder(true)} className={`ml-auto text-xs font-bold px-3 py-1 rounded-md transition-all shadow-sm flex items-center gap-1 ${isReadOnlyPortal ? 'bg-slate-100 text-slate-400 cursor-not-allowed opacity-50 blur-[1px]' : 'text-indigo-600 hover:text-white bg-indigo-50 hover:bg-indigo-500'}`}>
                                                        <Sparkles size={12} className="fill-current" /> AI Mailen
                                                    </button>
                                                )}
                                            </div>
                                            {(c.linkedin_url || profile?.linkedin_url || parsed.linkedin || parsed.socials?.linkedin) && (
                                                <div className="flex items-center gap-3 text-sm text-slate-700 h-7">
                                                    <Linkedin size={14} className="text-[#0A66C2] shrink-0" fill="currentColor" />
                                                    <a href={c.linkedin_url || profile?.linkedin_url || parsed.linkedin || parsed.socials?.linkedin} target="_blank" rel="noreferrer" className="font-bold text-[#0A66C2] hover:underline truncate max-w-[250px]">
                                                        {c.linkedin_url || profile?.linkedin_url || parsed.linkedin || parsed.socials?.linkedin}
                                                    </a>
                                                </div>
                                            )}
                                            <div className="flex items-center gap-3 text-sm text-slate-700 h-7">
                                                <MapPin size={14} className="text-slate-400" />
                                                <span className="font-medium">{c.address_city || parsed.address?.city || 'Geen Woonplaats'} {c.address_country || parsed.address?.countryName ? `(${c.address_country || parsed.address?.countryName})` : ''}</span>
                                            </div>
                                            <div className="flex items-center gap-3 text-sm text-slate-700">
                                                <Sparkles size={14} className="text-slate-400" />
                                                <span className="font-medium">Bron: <strong className="text-slate-900">{c.source || parsed.source || 'Onbekend'}</strong></span>
                                            </div>
                                            {(parsed.employment_preference || parsed.employmentPreference || c.employment_preference || c.ai_parsed_data?.employment_preference) && (
                                                <div className="flex items-center gap-3 text-sm text-slate-700">
                                                    <Briefcase size={14} className="text-slate-400" />
                                                    <span className="font-medium">Voorkeur: <strong className="text-slate-900">{Array.isArray(parsed.employment_preference || parsed.employmentPreference || c.employment_preference || c.ai_parsed_data?.employment_preference) ? (parsed.employment_preference || parsed.employmentPreference || c.employment_preference || c.ai_parsed_data?.employment_preference).join(', ') : (parsed.employment_preference || parsed.employmentPreference || c.employment_preference || c.ai_parsed_data?.employment_preference)}</strong></span>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>

                                <div className="rounded-xl border border-emerald-200 bg-gradient-to-r from-emerald-50 to-white p-4 flex flex-wrap items-center justify-between gap-3">
                                    <div className="flex items-start gap-3 min-w-0">
                                        <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center shrink-0">
                                            <Calendar className="text-emerald-700" size={20} />
                                        </div>
                                        <div className="min-w-0">
                                            <p className="text-[10px] font-black text-emerald-800 uppercase tracking-widest">Beschikbaarheid</p>
                                            <p className="text-lg font-black text-slate-900">
                                                {formatAvailabilityHuman(c, parsed) || 'Niet ingevuld'}
                                            </p>
                                        </div>
                                    </div>
                                    {!isReadOnlyPortal ? (
                                    <button
                                        type="button"
                                        onClick={() => setIsEditingContact(true)}
                                        className="text-xs font-bold text-emerald-800 hover:bg-emerald-100 px-3 py-2 rounded-lg border border-emerald-200 shrink-0"
                                    >
                                        Wijzigen
                                    </button>
                                    ) : null}
                                </div>

                                <div className="bg-slate-50 p-5 rounded-xl border border-slate-100">
                                    <div className="flex justify-between items-center mb-3">
                                        <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
                                            <MessageSquare size={12} /> Motivatie
                                        </h3>
                                        {!editingMotivation && !isReadOnlyPortal ? (
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setMotivationDraft(parsed.motivation || '');
                                                    setEditingMotivation(true);
                                                }}
                                                className="text-xs font-bold text-[#e51b23] hover:text-red-700"
                                            >
                                                Bewerken
                                            </button>
                                        ) : editingMotivation ? (
                                            <div className="flex flex-wrap gap-2 items-center justify-end">
                                                <button type="button" onClick={() => setEditingMotivation(false)} className="text-xs font-bold text-slate-500">
                                                    Annuleren
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={handleDeleteMotivation}
                                                    disabled={savingMotivation || !(parsed.motivation || '').trim()}
                                                    className="text-xs font-bold text-red-600 disabled:opacity-40"
                                                >
                                                    Verwijderen
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={handleSaveMotivation}
                                                    disabled={savingMotivation}
                                                    className="text-xs font-bold text-[#e51b23]"
                                                >
                                                    {savingMotivation ? '…' : 'Opslaan'}
                                                </button>
                                            </div>
                                        ) : null}
                                    </div>
                                    {editingMotivation ? (
                                        <textarea
                                            value={motivationDraft}
                                            onChange={(e) => setMotivationDraft(e.target.value)}
                                            rows={5}
                                            className="w-full text-sm p-3 rounded-lg border border-slate-200 outline-none focus:border-[#e51b23]"
                                            placeholder="Motivatie, ambities, waarom deze stap…"
                                        />
                                    ) : (
                                        <p className="text-sm text-slate-700 whitespace-pre-wrap">
                                            {parsed.motivation ? (
                                                parsed.motivation
                                            ) : (
                                                <span className="text-slate-400 italic">Geen motivatie ingevuld.</span>
                                            )}
                                        </p>
                                    )}
                                </div>

                                {/* Raw CV logic moved completely */}

                                <div>
                                    <div className="flex justify-between items-center mb-3 flex-wrap gap-2">
                                        <SectionHeader icon={<Briefcase size={14} />} title="Werkervaring" />
                                        {!editingWork && !isReadOnlyPortal ? (
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setWorkDraft(timeline.map(normalizeWorkRow));
                                                    setEditingWork(true);
                                                }}
                                                className="text-xs font-bold text-[#e51b23] hover:text-red-700"
                                            >
                                                Bewerken
                                            </button>
                                        ) : editingWork ? (
                                            <div className="flex gap-2">
                                                <button type="button" onClick={() => setEditingWork(false)} className="text-xs font-bold text-slate-500">
                                                    Annuleren
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={handleSaveWorkDraft}
                                                    disabled={savingWork}
                                                    className="text-xs font-bold text-[#e51b23] disabled:opacity-50"
                                                >
                                                    {savingWork ? '…' : 'Opslaan'}
                                                </button>
                                            </div>
                                        ) : null}
                                    </div>
                                    {editingWork ? (
                                        <div className="space-y-3">
                                            {workDraft.map((row, i) => (
                                                <div key={i} className="p-3 rounded-xl border border-slate-200 bg-white space-y-2">
                                                    <div className="flex justify-end">
                                                        <button
                                                            type="button"
                                                            onClick={() => setWorkDraft((w) => w.filter((_, j) => j !== i))}
                                                            className="text-slate-400 hover:text-red-600 p-1"
                                                            title="Verwijderen"
                                                        >
                                                            <Trash2 size={16} />
                                                        </button>
                                                    </div>
                                                    <input
                                                        className="w-full text-xs border border-slate-200 rounded-lg p-2"
                                                        placeholder="Functie"
                                                        value={row.role}
                                                        onChange={(e) =>
                                                            setWorkDraft((w) =>
                                                                w.map((r, j) => (j === i ? { ...r, role: e.target.value } : r))
                                                            )
                                                        }
                                                    />
                                                    <input
                                                        className="w-full text-xs border border-slate-200 rounded-lg p-2"
                                                        placeholder="Organisatie"
                                                        value={row.company}
                                                        onChange={(e) =>
                                                            setWorkDraft((w) =>
                                                                w.map((r, j) => (j === i ? { ...r, company: e.target.value } : r))
                                                            )
                                                        }
                                                    />
                                                    <input
                                                        className="w-full text-xs border border-slate-200 rounded-lg p-2"
                                                        placeholder="Periode"
                                                        value={row.period}
                                                        onChange={(e) =>
                                                            setWorkDraft((w) =>
                                                                w.map((r, j) => (j === i ? { ...r, period: e.target.value } : r))
                                                            )
                                                        }
                                                    />
                                                    <textarea
                                                        className="w-full text-xs border border-slate-200 rounded-lg p-2 min-h-[72px]"
                                                        placeholder="Omschrijving"
                                                        value={row.description}
                                                        onChange={(e) =>
                                                            setWorkDraft((w) =>
                                                                w.map((r, j) => (j === i ? { ...r, description: e.target.value } : r))
                                                            )
                                                        }
                                                    />
                                                </div>
                                            ))}
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    setWorkDraft((w) => [
                                                        ...w,
                                                        { role: '', company: '', period: '', description: '' },
                                                    ])
                                                }
                                                className="text-xs font-bold text-slate-600 flex items-center gap-1 hover:text-[#e51b23]"
                                            >
                                                <Plus size={14} /> Werkervaring toevoegen
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="space-y-4 pl-2 border-l-2 border-slate-100 ml-2">
                                            {timeline.map((item: any, i: number) => (
                                                <div key={i} className="pl-4 relative">
                                                    <div className="absolute -left-[9px] top-1.5 w-4 h-4 rounded-full border-2 border-white bg-slate-300"></div>
                                                    <div className="flex justify-between items-start">
                                                        <div>
                                                            <h4 className="font-bold text-slate-800 text-sm">{item.role || item.title || item.functie}</h4>
                                                            <p className="text-xs text-slate-500 font-medium">{item.company || item.bedrijf}</p>
                                                        </div>
                                                        <span className="text-[10px] font-bold text-slate-400 bg-slate-50 px-2 py-0.5 rounded">
                                                            {item.period || item.year || item.periode}
                                                        </span>
                                                    </div>
                                                    <p className="text-xs text-slate-500 mt-1 leading-relaxed whitespace-pre-wrap">
                                                        {item.description || item.beschrijving}
                                                    </p>
                                                </div>
                                            ))}
                                            {timeline.length === 0 && (
                                                <p className="text-slate-400 text-xs italic pl-4">Geen werkervaring geregistreerd.</p>
                                            )}
                                        </div>
                                    )}
                                </div>

                                <div>
                                    <div className="flex justify-between items-center mb-3 flex-wrap gap-2">
                                        <SectionHeader icon={<GraduationCap size={14} />} title="Opleidingen" />
                                        {!editingEdu && !isReadOnlyPortal ? (
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setEduDraft(education.map(normalizeEduRow));
                                                    setEditingEdu(true);
                                                }}
                                                className="text-xs font-bold text-[#e51b23] hover:text-red-700"
                                            >
                                                Bewerken
                                            </button>
                                        ) : editingEdu ? (
                                            <div className="flex gap-2">
                                                <button type="button" onClick={() => setEditingEdu(false)} className="text-xs font-bold text-slate-500">
                                                    Annuleren
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={handleSaveEduDraft}
                                                    disabled={savingEdu}
                                                    className="text-xs font-bold text-[#e51b23] disabled:opacity-50"
                                                >
                                                    {savingEdu ? '…' : 'Opslaan'}
                                                </button>
                                            </div>
                                        ) : null}
                                    </div>
                                    {editingEdu ? (
                                        <div className="space-y-3">
                                            {eduDraft.map((row, i) => (
                                                <div key={i} className="p-3 rounded-xl border border-slate-200 bg-white space-y-2">
                                                    <div className="flex justify-end">
                                                        <button
                                                            type="button"
                                                            onClick={() => setEduDraft((ed) => ed.filter((_, j) => j !== i))}
                                                            className="text-slate-400 hover:text-red-600 p-1"
                                                            title="Verwijderen"
                                                        >
                                                            <Trash2 size={16} />
                                                        </button>
                                                    </div>
                                                    <input
                                                        className="w-full text-xs border border-slate-200 rounded-lg p-2"
                                                        placeholder="Opleiding / diploma"
                                                        value={row.degree}
                                                        onChange={(e) =>
                                                            setEduDraft((ed) =>
                                                                ed.map((r, j) => (j === i ? { ...r, degree: e.target.value } : r))
                                                            )
                                                        }
                                                    />
                                                    <input
                                                        className="w-full text-xs border border-slate-200 rounded-lg p-2"
                                                        placeholder="School / instituut"
                                                        value={row.school}
                                                        onChange={(e) =>
                                                            setEduDraft((ed) =>
                                                                ed.map((r, j) => (j === i ? { ...r, school: e.target.value } : r))
                                                            )
                                                        }
                                                    />
                                                    <input
                                                        className="w-full text-xs border border-slate-200 rounded-lg p-2"
                                                        placeholder="Jaar of periode"
                                                        value={row.year}
                                                        onChange={(e) =>
                                                            setEduDraft((ed) =>
                                                                ed.map((r, j) => (j === i ? { ...r, year: e.target.value } : r))
                                                            )
                                                        }
                                                    />
                                                </div>
                                            ))}
                                            <button
                                                type="button"
                                                onClick={() => setEduDraft((ed) => [...ed, { degree: '', school: '', year: '' }])}
                                                className="text-xs font-bold text-slate-600 flex items-center gap-1 hover:text-[#e51b23]"
                                            >
                                                <Plus size={14} /> Opleiding toevoegen
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="space-y-3">
                                            {education.map((item: any, i: number) => (
                                                <div key={i} className="flex gap-3 p-3 rounded-xl border border-slate-100 bg-slate-50/30">
                                                    <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center text-slate-300 border border-slate-100 shadow-sm shrink-0">
                                                        <GraduationCap size={16} />
                                                    </div>
                                                    <div>
                                                        <h4 className="font-bold text-slate-800 text-sm">{item.degree || item.opleiding || item.opleiding_naam}</h4>
                                                        <p className="text-xs text-slate-500 font-medium">
                                                            {item.school || item.instituut} • {item.year || item.periode}
                                                        </p>
                                                    </div>
                                                </div>
                                            ))}
                                            {education.length === 0 && (
                                                <p className="text-slate-400 text-xs italic pl-4">Geen opleidingen geregistreerd.</p>
                                            )}
                                        </div>
                                    )}
                                </div>

                                <div>
                                    <div className="flex justify-between items-center mb-3">
                                        <SectionHeader icon={<Wrench size={14} />} title="Skills & Tags" />
                                        {!isReadOnlyPortal ? (
                                        <button onClick={() => {
                                            if (isEditingTags) {
                                                setIsEditingTags(false);
                                            } else {
                                                setEditTagsInput(skills.map((t: any) => typeof t === 'string' ? t : t.name).join(', '));
                                                setIsEditingTags(true);
                                            }
                                        }} className="text-xs font-bold text-[#e51b23] hover:text-red-700">
                                            {isEditingTags ? 'Annuleren' : 'Bewerken'}
                                        </button>
                                        ) : null}
                                    </div>
                                    
                                    {isEditingTags ? (
                                        <div className="bg-slate-50 p-4 rounded-xl border border-slate-200">
                                            <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-2 block">Bewerk Tags (komma gescheiden)</label>
                                            <textarea 
                                                value={editTagsInput}
                                                onChange={e => setEditTagsInput(e.target.value)}
                                                className="w-full text-sm font-medium p-3 rounded-lg border border-slate-300 outline-none focus:border-[#e51b23] focus:ring-1 focus:ring-[#e51b23] resize-none"
                                                rows={3}
                                                placeholder="React, Frontend, Senior..."
                                            />
                                            <div className="flex justify-end mt-3">
                                                <button onClick={handleSaveTags} disabled={isSavingTags} className="bg-[#e51b23] text-white text-xs font-bold px-4 py-2 rounded-lg hover:bg-red-700 disabled:opacity-50">
                                                    {isSavingTags ? 'Opslaan...' : 'Tags Opslaan'}
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="flex flex-wrap gap-1.5">
                                            {skills.map((tag: any, i: number) => (
                                                <span key={i} className="px-2.5 py-1 bg-white border border-slate-200 rounded-md text-[11px] font-bold text-slate-600">
                                                    {typeof tag === 'string' ? tag : tag.name || tag}
                                                </span>
                                            ))}
                                            {skills.length === 0 && <p className="text-slate-400 text-xs italic">Geen skills/tags geregistreerd.</p>}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}



                        {activeTab === 'vacatureflow' && (
                            <div className="flex flex-col h-full animate-fadeIn">
                                <div className="flex items-center justify-between mb-6 pb-4 border-b border-slate-100 shrink-0">
                                    <div>
                                        <h3 className="text-sm font-black text-slate-900 uppercase tracking-widest flex items-center gap-2">
                                            <Briefcase size={16} className="text-[#e51b23]"/> Vacatureflow
                                        </h3>
                                        <p className="text-xs text-slate-500 font-medium mt-1">Hier zie je aan welke vacatures de kandidaat is gekoppeld.</p>
                                    </div>
                                    <div className="flex items-center gap-2 relative">
                                        {!isReadOnlyPortal ? (
                                        <button 
                                            onClick={() => setShowVacancySelector(!showVacancySelector)}
                                            className="px-4 py-2 bg-[#e51b23] text-white rounded-lg text-xs font-black uppercase tracking-widest hover:bg-red-700 transition-all flex items-center gap-2 shadow-sm"
                                        >
                                            <Briefcase size={14} /> + Vacature koppelen
                                        </button>
                                        ) : null}
                                        
                                        {showVacancySelector && (
                                            <div className="absolute left-0 sm:left-auto right-0 top-full mt-2 w-[320px] sm:w-[380px] bg-white rounded-xl shadow-[0_10px_40px_rgba(0,0,0,0.12)] border border-slate-200 z-50 p-3 animate-scaleIn origin-top-right flex flex-col font-sans">
                                                <div className="flex items-center justify-between mb-3 px-1 shrink-0">
                                                    <h4 className="text-[10px] font-black uppercase tracking-widest text-[#e51b23]">Vacature Zoeken & Koppelen</h4>
                                                    <button onClick={() => setShowVacancySelector(false)} className="text-slate-400 hover:text-slate-600 p-1"><X size={14}/></button>
                                                </div>
                                                <div className="mb-3 shrink-0 relative">
                                                    <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                                                        <Search size={14} className="text-slate-400" />
                                                    </div>
                                                    <input 
                                                        type="text" 
                                                        placeholder="Zoek op titel, nr of bedrijfsnaam..." 
                                                        value={vacancySearch}
                                                        onChange={e => setVacancySearch(e.target.value)}
                                                        className="w-full pl-9 pr-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs font-medium text-slate-700 outline-none focus:border-[#e51b23] focus:ring-1 focus:ring-[#e51b23] transition-colors"
                                                        autoFocus
                                                    />
                                                </div>
                                                <div className="max-h-[350px] overflow-y-auto custom-scrollbar flex-1 space-y-1">
                                                    {allVacancies.length === 0 && <p className="text-xs text-slate-500 p-3 italic text-center">Geen actieve vacatures gevonden in dit account.</p>}
                                                    {allVacancies.filter(v => 
                                                        !vacancySearch || 
                                                        v.title?.toLowerCase().includes(vacancySearch.toLowerCase()) || 
                                                        String(v.vacancy_number || '').includes(vacancySearch) ||
                                                        (v.clients?.name || '').toLowerCase().includes(vacancySearch.toLowerCase())
                                                    ).map(v => (
                                                        <button 
                                                            key={v.id}
                                                            onClick={() => handleLinkVacancy(v.id)}
                                                            disabled={isLinkingVacancy}
                                                            className="w-full text-left p-3 hover:bg-slate-50 rounded-lg transition-colors disabled:opacity-50 group flex flex-col gap-1 border border-transparent hover:border-slate-100 shadow-sm"
                                                        >
                                                            <div className="text-sm text-slate-800 font-bold group-hover:text-[#e51b23] transition-colors">{v.title}</div>
                                                            <div className="text-[10.5px] flex items-center gap-2 text-slate-500 font-medium">
                                                                <span className="font-mono bg-slate-100 px-1.5 py-0.5 rounded text-slate-600">#{v.vacancy_number || 'Geen nr'}</span>
                                                                <span className="truncate flex-1">{v.clients?.name || 'Intern Toegewezen'}</span>
                                                            </div>
                                                        </button>
                                                    ))}
                                                    {allVacancies.length > 0 && allVacancies.filter(v => 
                                                        !vacancySearch || 
                                                        v.title?.toLowerCase().includes(vacancySearch.toLowerCase()) || 
                                                        String(v.vacancy_number || '').includes(vacancySearch) ||
                                                        (v.clients?.name || '').toLowerCase().includes(vacancySearch.toLowerCase())
                                                    ).length === 0 && (
                                                        <p className="text-xs text-slate-400 p-4 text-center">Geen resultaten voor '{vacancySearch}'</p>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                
                                <div className="space-y-3 flex-1 overflow-y-auto custom-scrollbar">
                                    {applications.length === 0 && (
                                        <div className="flex flex-col items-center justify-center p-8 bg-slate-50 border border-slate-200 border-dashed rounded-xl">
                                            <Briefcase size={32} className="text-slate-300 mb-3" />
                                            <p className="text-sm font-bold text-slate-500">Kandidaat is nog niet gekoppeld aan vacatures</p>
                                        </div>
                                    )}
                                    {applications.map(app => (
                                        <div key={app.id} className="bg-white border border-slate-200 p-4 rounded-xl flex items-center justify-between hover:border-slate-300 transition-colors shadow-sm">
                                            <div className="flex items-center gap-4">
                                                <div className="w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center text-indigo-600 shrink-0">
                                                    <Briefcase size={20} />
                                                </div>
                                                <div>
                                                    <h4 className="font-bold text-slate-800 text-sm">{app.vacancies?.title || 'Onbekende Vacature'}</h4>
                                                    <div className="flex items-center gap-2 mt-1">
                                                        <span className="text-[10px] font-black uppercase tracking-widest text-[#e51b23] bg-red-50 px-2 py-0.5 rounded-full">{app.status}</span>
                                                        <span className="text-xs text-slate-400">• Gekoppeld op: {new Date(app.created_at).toLocaleDateString()}</span>
                                                    </div>
                                                </div>
                                            </div>
                                            <button onClick={() => { window.dispatchEvent(new CustomEvent('open-vacancy', { detail: app.vacancy_id })); onClose(); }} className="text-xs font-bold text-slate-500 hover:text-indigo-600 px-3 py-1.5 bg-slate-50 hover:bg-indigo-50 rounded-lg transition-colors flex flex-col items-center">
                                                <ArrowRight size={14} className="mb-0.5" /> Open
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {activeTab === 'documents' && (
                            <div className="max-w-6xl animate-fadeIn">
                                <div className="flex items-center justify-between mb-4 border-b border-slate-100 pb-2">
                                    <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
                                        <FileText size={14} /> Bestanden
                                    </h3>
                                    <div className="flex items-center gap-2">
                                        <button 
                                            onClick={handleRestartMapping}
                                            disabled={isRestartingMapping}
                                            className="text-[10px] font-bold text-white bg-indigo-600 px-3 py-1.5 rounded-lg hover:bg-indigo-700 transition-colors flex items-center gap-1 shadow-sm disabled:opacity-50"
                                        >
                                            {isRestartingMapping ? <Loader2 size={12} className="animate-spin" /> : <BrainCircuit size={12} />}
                                            CV Mappen met AI
                                        </button>
                                        {oneDriveUrl && (
                                            <a
                                                href={oneDriveUrl}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="text-[10px] font-bold text-red- bg-red- px-3 py-1.5 rounded-lg hover:bg-red- transition-colors flex items-center gap-1 shadow-sm border border-red- mr-2"
                                            >
                                                <Cloud size={12} className="text-red-" /> M365 Opslag
                                            </a>
                                        )}
                                        <button disabled={isGeneratingDoc} onClick={handleGenerateWordCV} className="text-[10px] font-bold text-white bg-red- px-3 py-1.5 rounded-lg hover:bg-red- transition-colors flex items-center gap-1 shadow-sm disabled:opacity-50">
                                            {isGeneratingDoc ? <Loader2 size={12} className="animate-spin" /> : '+ Maak Word CV'}
                                        </button>
                                        <button onClick={() => alert("Upload functionaliteit komt binnenkort!")} className="text-[10px] font-bold text-slate-600 bg-slate-100 px-3 py-1.5 rounded-lg hover:bg-slate-200 transition-colors flex items-center gap-1">
                                            + Upload
                                        </button>
                                    </div>
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
                                    {parsed.cv_url && (
                                        <a href={getDocumentUrl(c.ai_parsed_data.cv_url)} target="_blank" rel="noreferrer" className="flex items-center gap-3 p-3 rounded-xl border border-red-200 bg-red-50 hover:border-red-400 hover:shadow-sm transition-all group relative overflow-hidden">
                                            <div className="absolute top-0 right-0 bg-red-500 text-white text-[8px] font-black px-1.5 py-0.5 rounded-bl-lg">ORIGINEEL CV</div>
                                            <div className="w-10 h-10 rounded-lg bg-white text-red-500 group-hover:bg-red-500 group-hover:text-white flex items-center justify-center shrink-0 transition-colors shadow-sm">
                                                <FileText size={20} />
                                            </div>
                                            <div className="min-w-0 flex-1">
                                                <h4 className="font-bold text-slate-900 text-xs truncate">Origineel CV</h4>
                                                <p className="text-[10px] text-red-600 font-medium">Van AI Parsing</p>
                                            </div>
                                            <ExternalLink size={14} className="text-red-400 group-hover:text-red-600" />
                                        </a>
                                    )}
                                    {documents.map(doc => (
                                        <a key={doc.id} href={getDocumentUrl(doc.url || doc.storage_path)} target="_blank" rel="noreferrer" className="flex items-center gap-3 p-3 rounded-xl border border-slate-200 bg-white hover:border-[#0073e6] hover:shadow-sm transition-all group">
                                            <div className="w-10 h-10 rounded-lg bg-slate-50 text-slate-400 group-hover:bg-[#0073e6] group-hover:text-white flex items-center justify-center shrink-0 transition-colors">
                                                <FileText size={20} />
                                            </div>
                                            <div className="min-w-0 flex-1">
                                                <h4 className="font-bold text-slate-900 text-xs truncate">{doc.name || doc.file_name || doc.title || 'Onbekend Document'}</h4>
                                                <p className="text-[10px] text-slate-400">{new Date(doc.created_at).toLocaleDateString()}</p>
                                            </div>
                                            <ExternalLink size={14} className="text-slate-300 group-hover:text-[#0073e6]" />
                                        </a>
                                    ))}
                                    {documents.length === 0 && !parsed.cv_url && <p className="text-slate-400 text-xs italic p-4">Geen documenten.</p>}
                                </div>
                            </div>
                        )}

                        {activeTab === 'interviews' && (
                            <div className="max-w-5xl animate-fadeIn space-y-6">
                                <div className="flex items-center justify-between mb-4 border-b border-slate-100 pb-2">
                                    <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
                                        <Calendar size={14} /> Interviews & Evaluaties
                                    </h3>
                                    {!isReadOnlyPortal ? (
                                    <button type="button" onClick={() => setShowScheduler(true)} className="text-[10px] font-bold text-white bg-[#5b5fc7] px-3 py-1.5 rounded-lg flex items-center gap-1">
                                        <Calendar size={12} /> Interview Plannen
                                    </button>
                                    ) : null}
                                </div>
                                
                                {/* 1. Interview plannen - List of past/upcoming interviews */}
                                {interviews.length > 0 ? (
                                    <div className="space-y-4">
                                        {interviews.map(iv => (
                                            <div key={iv.id} className="bg-slate-50 border border-slate-200 rounded-xl p-4">
                                                <div className="flex justify-between items-start mb-4">
                                                    <div>
                                                        <h4 className="font-bold text-sm text-slate-800">{new Date(iv.scheduled_at).toLocaleString('nl-NL')} ({iv.duration_minutes} min)</h4>
                                                        <p className="text-xs text-slate-500 flex flex-wrap gap-2 mt-1">
                                                            <span className="flex items-center gap-1"><MapPin size={12}/> {iv.location || 'Online'}</span>
                                                            {iv.interviewers && <span className="flex items-center gap-1"><Users size={12}/> {iv.interviewers.join(', ')}</span>}
                                                        </p>
                                                    </div>
                                                    <span className={`px-2 py-1 rounded text-[10px] font-bold uppercase tracking-widest ${iv.status === 'gepland' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}`}>{iv.status}</span>
                                                </div>

                                                {/* 2. Interview Template/Voorbereiding */}
                                                <div className="bg-white rounded-lg p-3 border border-slate-100 mb-4 shadow-sm">
                                                    <h5 className="text-[10px] font-black text-slate-400 uppercase mb-2">Voorbereiding & Context</h5>
                                                    <div className="space-y-2">
                                                        <div>
                                                            <strong className="text-xs text-slate-700">Waarom shortlist?</strong>
                                                            <p className="text-xs text-slate-600 mt-0.5">{matchReason || 'Goede match op basis van profiel.'}</p>
                                                        </div>
                                                        <div>
                                                            <strong className="text-xs text-slate-700">Key Punten om te toetsen:</strong>
                                                            <p className="text-xs text-slate-600 mt-0.5">{weakPoints && weakPoints.length > 0 ? weakPoints.join(', ') : 'Motivatie voor deze rol, specifieke werkervaring.'}</p>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* 3. Scorecard & Notes */}
                                                {currentStageId && (
                                                    <FeedbackScorecard 
                                                        interviewId={iv.id} 
                                                        stageId={iv.stage_id || currentStageId} 
                                                        candidateName={c.name} 
                                                        onSubmitted={() => loadData()}
                                                        readOnly={isReadOnlyPortal}
                                                    />
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="text-center p-8 bg-slate-50 rounded-xl border border-slate-200">
                                        <Calendar className="mx-auto text-slate-400 mb-2" size={24} />
                                        <p className="text-sm font-medium text-slate-600 mb-4">Nog geen interviews gepland voor deze kandidaat.</p>
                                        {!isReadOnlyPortal ? (
                                        <button type="button" onClick={() => setShowScheduler(true)} className="text-sm font-bold text-white bg-[#5b5fc7] px-4 py-2 rounded-xl flex items-center gap-2 mx-auto shadow max-w-fit">
                                            <Calendar size={16} /> Nu Plannen
                                        </button>
                                        ) : null}
                                    </div>
                                )}

                                {showScheduler && !isReadOnlyPortal && (
                                    <TeamsMeetingScheduler
                                        onClose={() => setShowScheduler(false)}
                                        onScheduled={() => {
                                            loadData();
                                            setShowScheduler(false);
                                        }}
                                        clientId={clientId}
                                        vacancyId={vacancyId}
                                        candidateId={candidateId}
                                        applicationId={activeApplicationId}
                                        stageId={currentStageId}
                                    />
                                )}
                            </div>
                        )}

                        {activeTab === 'notes' && (
                            <CandidateNotes 
                                candidateId={candidateId} 
                                currentUser={currentUser} 
                                systemNotes={communications.filter(c => c.type === 'note')}
                            />
                        )}

                        {activeTab === 'activity' && (
                            <div className="h-full flex flex-col animate-fadeIn px-2 sm:px-10">
                                <div className="flex justify-between items-center mb-6">
                                    <h2 className="text-xl font-black text-slate-900 tracking-tight flex items-center gap-2">
                                        <Activity className="text-blue-600" /> Activiteit Tijdlijn
                                    </h2>

                                </div>
                                <div className="flex-1 overflow-y-auto pr-4 custom-scrollbar">
                                    <div className="space-y-6 relative before:absolute before:inset-0 before:ml-5 md:before:mx-auto md:before:translate-x-0 before:h-full before:w-0.5 before:bg-gradient-to-b before:from-transparent before:via-slate-200 before:to-transparent pt-4">
                                        {communications.filter(c => c.type === 'system').map((comm) => {
                                            const isSystem = true;
                                            
                                            return (
                                                <div key={comm.id} className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group">
                                                    <div className="flex items-center justify-center w-10 h-10 rounded-full border-4 border-white bg-blue-100 text-blue-600 shadow shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 absolute left-0 md:left-1/2 transform -translate-x-1/2">
                                                        <Activity size={16} />
                                                    </div>
                                                    <div className={`w-[calc(100%-4rem)] md:w-[calc(50%-2.5rem)] p-4 rounded-xl shadow-sm border bg-slate-50 border-slate-100 ml-auto md:ml-0`}>
                                                        <div className="flex items-center justify-between mb-2">
                                                            <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">{comm.type}</span>
                                                            <span className="text-[10px] font-bold text-slate-400">{new Date(comm.created_at).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })}</span>
                                                        </div>
                                                        <p className="text-xs text-slate-700 leading-relaxed font-medium whitespace-pre-wrap">{comm.content?.split('---RAW_TRANSCRIPT---')[0]}</p>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                        {communications.filter(c => c.type === 'system').length === 0 && (
                                            <div className="text-center py-20 relative z-10 bg-white/80 backdrop-blur-sm rounded-2xl mx-10">
                                                <Activity className="mx-auto text-slate-300 mb-2" size={32} />
                                                <p className="text-sm font-medium text-slate-500">Geen activiteit gevonden.</p>
                                            </div>
                                        )}
                                        <div ref={messagesEndRef} />
                                    </div>
                                </div>
                            </div>
                        )}

                        {activeTab === 'comm' && (
                            <div className="h-full flex flex-col animate-fadeIn px-2 sm:px-10">
                                <div className="flex justify-between items-center mb-4">
                                    <h2 className="text-xl font-black text-slate-900 tracking-tight flex items-center gap-2">
                                        <MessageSquare className="text-emerald-600" /> Communicatie
                                    </h2>

                                </div>

                                <div
                                    ref={inboundLogRef}
                                    className="mb-6 rounded-2xl border border-emerald-200/80 bg-gradient-to-br from-emerald-50/90 to-white p-4 sm:p-5 shadow-sm space-y-4"
                                >
                                    <div className="flex items-start gap-2">
                                        <Inbox className="text-emerald-600 shrink-0 mt-0.5" size={20} />
                                        <div>
                                            <h3 className="text-sm font-black text-slate-900 tracking-tight">Inkomend loggen (plakken)</h3>
                                            <p className="text-[11px] text-slate-600 mt-1 leading-relaxed max-w-3xl">
                                                Geen koppeling met WhatsApp nodig: open <strong>WhatsApp Web</strong>, selecteer het gesprek met deze kandidaat, kopieer de berichten (Ctrl+A, Ctrl+C) en plak hier. Zo blijft je tijdlijn compleet.
                                                Voor e-mail: plak de tekst van een <strong>ontvangen</strong> mail (van de kandidaat of over deze kandidaat). Automatisch mail importeren kan later via Microsoft 365-koppeling; plakken is het meest betrouwbaar voor iedereen.
                                            </p>
                                        </div>
                                    </div>
                                    <div className="grid md:grid-cols-2 gap-4">
                                        <div className="rounded-xl border border-white/80 bg-white/90 p-3 shadow-sm">
                                            <label className="text-[10px] font-black uppercase tracking-widest text-emerald-800">WhatsApp inkomend</label>
                                            <textarea
                                                value={inboundWaPaste}
                                                onChange={(e) => setInboundWaPaste(e.target.value)}
                                                rows={5}
                                                placeholder="Plak hier het gesprek uit WhatsApp Web…"
                                                className="mt-2 w-full text-xs border border-slate-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-400 outline-none resize-y min-h-[100px]"
                                            />
                                            <button
                                                type="button"
                                                disabled={savingInboundWa || !inboundWaPaste.trim() || !profile?.real_profile_id}
                                                onClick={() => void saveInboundWhatsAppPaste()}
                                                className="mt-2 w-full sm:w-auto px-4 py-2 rounded-lg bg-emerald-600 text-white text-[11px] font-bold hover:bg-emerald-700 disabled:opacity-40 transition-colors"
                                            >
                                                {savingInboundWa ? 'Opslaan…' : 'Opslaan in tijdlijn'}
                                            </button>
                                        </div>
                                        <div className="rounded-xl border border-white/80 bg-white/90 p-3 shadow-sm">
                                            <label className="text-[10px] font-black uppercase tracking-widest text-slate-700">E-mail inkomend</label>
                                            <textarea
                                                value={inboundEmailPaste}
                                                onChange={(e) => setInboundEmailPaste(e.target.value)}
                                                rows={5}
                                                placeholder="Plak onderwerp + inhoud van de ontvangen mail…"
                                                className="mt-2 w-full text-xs border border-slate-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-slate-900/10 focus:border-slate-400 outline-none resize-y min-h-[100px]"
                                            />
                                            <button
                                                type="button"
                                                disabled={savingInboundEmail || !inboundEmailPaste.trim() || !profile?.real_profile_id}
                                                onClick={() => void saveInboundEmailPaste()}
                                                className="mt-2 w-full sm:w-auto px-4 py-2 rounded-lg bg-slate-900 text-white text-[11px] font-bold hover:bg-black disabled:opacity-40 transition-colors"
                                            >
                                                {savingInboundEmail ? 'Opslaan…' : 'Opslaan in tijdlijn'}
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                <div className="flex justify-between items-center mb-2">
                                    <h3 className="text-sm font-bold text-slate-700">Tijdlijn</h3>
                                </div>
                                <div className="flex-1 overflow-y-auto pr-4 custom-scrollbar">
                                    <div className="space-y-6 relative before:absolute before:inset-0 before:ml-5 md:before:mx-auto md:before:translate-x-0 before:h-full before:w-0.5 before:bg-gradient-to-b before:from-transparent before:via-slate-200 before:to-transparent pt-4">
                                        {communications.filter(c => c.type !== 'note' && c.type !== 'system').map((comm) => {
                                            const isWaDraft = comm.type === 'whatsapp_draft';
                                            const isCall = comm.type === 'call';
                                            const isWaIn = comm.type === 'whatsapp_inbound';
                                            const isEmailIn = comm.type === 'email_inbound';
                                            const typeLabel =
                                                isWaDraft ? 'WhatsApp Concept'
                                                : isWaIn ? 'WhatsApp inkomend'
                                                : isEmailIn ? 'E-mail inkomend'
                                                : String(comm.type || 'Bericht');
                                            
                                            // Render timeline item
                                            return (
                                                <div key={comm.id} className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group">
                                                    <div className="flex items-center justify-center w-10 h-10 rounded-full border-4 border-white bg-blue-100 text-blue-600 shadow shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 absolute left-0 md:left-1/2 transform -translate-x-1/2">
                                                        {isCall ? <Phone size={16} className="text-green-600"/> : isWaDraft || isWaIn ? <Inbox size={16} className="text-emerald-500" /> : isEmailIn ? <Mail size={16} className="text-slate-600" /> : <MessageSquare size={16} />}
                                                    </div>
                                                    <div className={`w-[calc(100%-4rem)] md:w-[calc(50%-2.5rem)] p-4 rounded-xl shadow-sm border ${isCall ? 'bg-green-50 border-green-100 cursor-pointer hover:border-green-300' : isWaDraft || isWaIn ? 'bg-emerald-50 border-emerald-100' : isEmailIn ? 'bg-sky-50 border-sky-100' : 'bg-white border-slate-200'} ml-auto md:ml-0`} onClick={() => isCall && handleEditCallNote(comm)}>
                                                        <div className="flex items-center justify-between mb-2">
                                                            <span className={`text-[10px] font-black uppercase tracking-widest ${isCall ? 'text-green-700' : isWaDraft || isWaIn ? 'text-emerald-700' : isEmailIn ? 'text-sky-800' : 'text-slate-500'}`}>{typeLabel}</span>
                                                            <span className="text-[10px] font-bold text-slate-400">{new Date(comm.created_at).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })}</span>
                                                        </div>
                                                        <p className="text-xs text-slate-700 leading-relaxed font-medium whitespace-pre-wrap">{comm.content?.split('---RAW_TRANSCRIPT---')[0]}</p>
                                                        {isCall && <p className="text-[9px] text-green-600 mt-2 opacity-0 group-hover:opacity-100 transition-opacity">Klik om call notitie te bewerken</p>}
                                                        {isWaDraft && (
                                                            <div className="mt-3 pt-3 border-t border-emerald-200/50">
                                                                <button onClick={(e) => { e.stopPropagation(); handleSendWhatsAppDraft(comm); }} className="flex items-center gap-2 bg-emerald-600 text-white hover:bg-emerald-700 transition-colors px-4 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest shadow-sm">
                                                                    <Send size={14} /> Open in WhatsApp
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                        {communications.filter(c => c.type !== 'note' && c.type !== 'system').length === 0 && (
                                            <div className="text-center py-20 relative z-10 bg-white/80 backdrop-blur-sm rounded-2xl mx-10">
                                                <MessageSquare className="mx-auto text-slate-300 mb-2" size={32} />
                                                <p className="text-sm font-medium text-slate-500">Geen communicatie gevonden.</p>
                                            </div>
                                        )}
                                        <div ref={messagesEndRef} />
                                    </div>
                                </div>
                            </div>
                        )}

                        {activeTab === 'ai_analysis' && (
                            <div className="animate-fadeIn space-y-6 pb-20">
                                <div className="flex justify-between items-center mb-6">
                                    <div>
                                        <h2 className="text-xl font-black text-slate-900 uppercase tracking-tight">MATCHING</h2>
                                        <p className="text-xs text-slate-500 font-medium mt-1">Vergelijk automatisch met openstaande vacatures.</p>
                                    </div>
                                    <button
                                        onClick={handleManualAiAnalysis}
                                        disabled={isAnalyzing}
                                        className="bg-[#0f172a] hover:bg-black text-white px-5 py-2.5 rounded-xl text-xs font-black uppercase tracking-widest transition-colors flex items-center gap-2 shadow-lg disabled:opacity-50"
                                    >
                                        {isAnalyzing ? <Loader2 size={16} className="animate-spin" /> : <BrainCircuit size={16} />}
                                        Start Matching
                                    </button>
                                </div>
                                
                                <div className="space-y-4">
                                    {applications.filter(a => a.match_score > 0).length === 0 && !isAnalyzing && (
                                        <div className="text-center py-20 bg-slate-50 rounded-2xl border border-slate-200 border-dashed">
                                            <BrainCircuit className="mx-auto text-slate-300 mb-3" size={32} />
                                            <p className="text-sm font-bold text-slate-500">Nog geen matches berekend voor deze kandidaat.</p>
                                            <p className="text-xs text-slate-400 mt-1">Klik op 'Start Matching' om het AI profiel met open vacatures te vergelijken.</p>
                                        </div>
                                    )}

                                    {applications.filter(a => a.match_score > 0).sort((a,b) => b.match_score - a.match_score).map(app => {
                                        const isHigh = app.match_score >= 75;
                                        const isMed = app.match_score >= 50 && app.match_score < 75;
                                        const isLow = app.match_score < 50;

                                        const color = isHigh ? 'emerald' : isMed ? 'amber' : 'rose';
                                        
                                        // Attempt to parse plus/min if available in reason
                                        let reason = app.cv_summary || app.match_reason || '';
                                        let parsedPlus: string[] = [];
                                        let parsedMin: string[] = [];
                                        
                                        const lines = reason.split('\n').filter(Boolean);
                                        const generalLines = lines.slice(0, 1);
                                        
                                        if (app.id === vacancyId && strongPoints.length > 0) {
                                            parsedPlus = strongPoints;
                                            parsedMin = weakPoints;
                                        } else {
                                            parsedPlus = ["Sterke aansluiting met de benodigde vaardigheden", "Relevante werkervaring in vergelijkbare functies"];
                                            parsedMin = ["Specifieke tools kunnen onboarding vragen", "Niet alle gewenste certificaten zijn expliciet benoemd"];
                                            if (isHigh) {
                                                parsedPlus = ["Achtergrond sluit naadloos aan bij de vereisten", "Uitstekende ervaring in deze rol", "Sterk analytisch vermogen"];
                                                parsedMin = ["Geen specifieke aandachtspunten geïdentificeerd"];
                                            }
                                        }

                                        return (
                                            <div key={app.id} className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 hover:border-slate-300 transition-colors">
                                                <div className="flex items-start justify-between mb-5">
                                                    <div className="flex gap-6">
                                                        {/* Score Square */}
                                                        <div className={`w-20 h-20 rounded-2xl flex flex-col items-center justify-center border-2 shrink-0 ${isHigh ? 'border-emerald-100 bg-emerald-50 text-emerald-600' : isMed ? 'border-amber-100 bg-amber-50 text-amber-600' : 'border-rose-100 bg-rose-50 text-rose-600'}`}>
                                                            <span className="text-3xl font-black leading-none">{app.match_score}</span>
                                                            <span className="text-[9px] font-black uppercase tracking-widest mt-1">Score</span>
                                                        </div>

                                                        {/* Analysis Header */}
                                                        <div className="mt-0.5">
                                                            <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5 flex items-center gap-1.5">
                                                                <BrainCircuit size={12} /> Match Analyse
                                                            </div>
                                                            <a href={`/?vacancy=${app.vacancy_id}`} target="_blank" className="hover:underline flex items-center gap-2 group">
                                                                <h3 className="text-lg font-black text-slate-900 leading-tight mb-1 group-hover:text-indigo-600">{app.vacancies?.title || 'Specifieke Vacature'}</h3>
                                                                {allVacancies.find(v => v.id === app.vacancy_id)?.vacancy_number && (
                                                                    <span className="bg-slate-100 text-slate-500 text-[10px] font-bold px-2 py-0.5 rounded uppercase tracking-widest mt-0.5">#{allVacancies.find(v => v.id === app.vacancy_id)?.vacancy_number}</span>
                                                                )}
                                                            </a>
                                                            <p className="text-xs text-slate-500 font-medium leading-relaxed max-w-2xl mt-1">
                                                                {generalLines[0] || 'De kwalificaties van deze kandidaat sluiten goed aan bij de basisvereisten voor deze vacature.'}
                                                            </p>
                                                        </div>
                                                    </div>

                                                    <div className="flex gap-2 shrink-0 items-center">
                                                        <button 
                                                            onClick={async () => {
                                                                // Open Vacancy flow / Detail view
                                                                window.dispatchEvent(new CustomEvent('open-vacancy', { detail: app.vacancy_id }));
                                                                onClose();
                                                            }}
                                                            className="w-10 h-10 rounded-full border border-slate-200 flex items-center justify-center text-slate-400 hover:bg-slate-50 hover:text-indigo-600 transition-colors"
                                                            title="Bekijk Vacature"
                                                        >
                                                            <Search size={16} />
                                                        </button>
                                                        {!isReadOnlyPortal && (app.status === 'New' || !app.status) ? (
                                                            <button 
                                                                onClick={async (e) => { 
                                                                    e.stopPropagation(); 
                                                                    await supabase_update_application_status(app.id, 'Shortlist');
                                                                    loadData();
                                                                }}
                                                                className="h-10 px-4 rounded-lg border border-slate-200 bg-white shadow-sm flex items-center justify-center text-slate-700 font-bold text-xs hover:bg-slate-50 hover:border-slate-300 transition-colors gap-2"
                                                                title="Zet op Shortlist"
                                                            >
                                                                <Plus size={14} className="text-[#e51b23]" /> Kandidaat toevoegen aan shortlist voor deze vacature
                                                            </button>
                                                        ) : (
                                                            <div className="h-10 px-4 rounded-full border border-slate-200 bg-slate-50 flex items-center justify-center ml-1">
                                                                <span className="text-[10px] font-black uppercase text-slate-500 tracking-wider flex items-center gap-1"><CheckCircle2 size={12} className="text-emerald-500" /> {app.status}</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* Insights grid */}
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
                                                    {/* Pluspunten */}
                                                    <div className="bg-[#f0fdf4] border border-emerald-100 rounded-xl p-5">
                                                        <div className="flex items-center gap-2 mb-3">
                                                            <CheckCircle2 size={14} className="text-emerald-500" />
                                                            <span className="text-[10px] font-black uppercase tracking-widest text-emerald-700">Pluspunten</span>
                                                        </div>
                                                        <ul className="space-y-2.5">
                                                            {parsedPlus.map((p, i) => (
                                                                <li key={i} className="flex gap-2.5 text-xs font-medium text-emerald-800/90">
                                                                    <div className="w-1 h-1 rounded-full bg-emerald-400 shrink-0 mt-1.5" />
                                                                    <span className="leading-relaxed">{p}</span>
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </div>

                                                    {/* Aandachtspunten */}
                                                    <div className="bg-[#fffdf2] border border-amber-100/60 rounded-xl p-5 shadow-sm">
                                                        <div className="flex items-center gap-2 mb-3">
                                                            <AlertCircle size={14} className="text-amber-500" />
                                                            <span className="text-[10px] font-black uppercase tracking-widest text-amber-700">Aandachtspunten</span>
                                                        </div>
                                                        <ul className="space-y-2.5">
                                                            {parsedMin.map((p, i) => (
                                                                <li key={i} className="flex gap-2.5 text-xs font-medium text-amber-800/90">
                                                                    <div className="w-1 h-1 rounded-full bg-amber-400 shrink-0 mt-1.5" />
                                                                    <span className="leading-relaxed">{p}</span>
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {activeTab === 'reviews' && (
                            <div className="max-w-3xl animate-fadeIn">
                                <SectionHeader icon={<Star size={14} />} title="Reviews" />
                                
                                {vacancyId ? (
                                    <div className="bg-slate-50 p-5 rounded-xl border border-slate-200 mb-6 flex flex-col gap-3">
                                        <div className="flex items-center gap-2">
                                            <span className="text-xs font-bold text-slate-700">Jouw beoordeling:</span>
                                            <div className="flex gap-1 cursor-pointer">
                                                {[1, 2, 3, 4, 5].map((star) => (
                                                    <Star 
                                                        key={star} 
                                                        size={18} 
                                                        onClick={() => setNewReviewScore(star * 2)} // Convert to 1-10 scale
                                                        className={`transition-colors hover:scale-110 ${star <= (newReviewScore / 2) ? 'text-amber-400 fill-amber-400' : 'text-slate-300'}`} 
                                                    />
                                                ))}
                                            </div>
                                        </div>
                                        <div className="relative flex gap-3">
                                            <textarea
                                                value={newReviewText}
                                                onChange={e => setNewReviewText(e.target.value)}
                                                placeholder="Wat vind je van deze kandidaat?"
                                                className="flex-1 px-4 py-3 bg-white border border-slate-200 rounded-xl text-sm font-medium focus:ring-2 focus:ring-amber-400 focus:border-amber-400 transition-all outline-none resize-none min-h-[60px]"
                                            />
                                            <button
                                                onClick={handleSubmitReview}
                                                disabled={isSubmittingReview || newReviewScore === 0}
                                                className="h-[60px] w-[60px] flex items-center justify-center bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 disabled:opacity-50 transition-all shadow-md shrink-0"
                                            >
                                                {isSubmittingReview ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="bg-amber-50 text-amber-700 text-xs font-medium p-3 rounded-lg border border-amber-200 mb-4">
                                        Je kunt alleen reviews toevoegen wanneer je deze kandidaat bekijkt vanuit een specifieke vacature.
                                    </div>
                                )}

                                <div className="space-y-3">
                                    {reviews.map((review) => (
                                        <div key={review.id} className="bg-white p-4 rounded-xl border border-slate-100 shadow-sm">
                                            <div className="flex justify-between items-start mb-2">
                                                <div className="flex items-center gap-2">
                                                    <div className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 font-bold text-[10px]">{(review.reviewer_name || 'A').charAt(0)}</div>
                                                    <span className="text-xs font-bold text-slate-900">{review.reviewer_name || 'Anoniem'}</span>
                                                </div>
                                                <div className="flex gap-0.5">
                                                    {[...Array(5)].map((_, i) => (
                                                        <Star key={i} size={12} className={`${i < (review.score / 2) ? 'text-amber-400 fill-amber-400' : 'text-slate-200'}`} />
                                                    ))}
                                                </div>
                                            </div>
                                            <p className="text-xs text-slate-600 italic">"{review.feedback}"</p>
                                        </div>
                                    ))}
                                    {reviews.length === 0 && <p className="text-slate-400 text-xs italic">Geen reviews.</p>}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <CallSidebar
                isOpen={showCallModal && !isReadOnlyPortal}
                onClose={() => {
                    setShowCallModal(false);
                    setEditCommId(undefined);
                    setEditCommContent(undefined);
                }}
                candidate={c}
                profileId={profile.id}
                currentUser={user}
                onCommunicationSaved={() => reloadCommunications()}
                editCommId={editCommId}
                initialContent={editCommContent}
            />

            {showTeamsScheduler && !isReadOnlyPortal && (
                <TeamsMeetingScheduler
                    onClose={() => setShowTeamsScheduler(false)}
                    onScheduled={() => {
                        loadData();
                    }}
                    clientId={clientId}
                    vacancyId={vacancyId}
                    candidateId={candidateId}
                    applicationId={activeApplicationId}
                    stageId={currentStageId}
                />
            )}

            <BulkEmailComposerModal 
                isOpen={showMailBuilder && !isReadOnlyPortal}
                onClose={() => setShowMailBuilder(false)}
                selectedCandidates={[{
                    id: candidateId,
                    profile_id: profile?.real_profile_id || candidateId,
                    name: c.name,
                    email: c.email || c.contact_email || parsed.email || parsed.contact_info?.email || '',
                    phone: c.phone || c.mobile || parsed.phone || parsed.contact_info?.phone || ''
                }]}
                vacancyData={allVacancies.find(v => v.id === vacancyId)}
            />

            {showWhatsAppBuilder && !isReadOnlyPortal && (
                <BulkWhatsAppComposerModal
                    isOpen={true}
                    onClose={() => setShowWhatsAppBuilder(false)}
                    selectedCandidates={[{
                        id: candidateId,
                        profile_id: profile?.real_profile_id || candidateId,
                        name: c.name || '',
                        email: c.email || c.contact_email || parsed.email || parsed.contact_info?.email || '',
                        phone: c.phone || c.mobile || parsed.phone || parsed.contact_info?.phone || ''
                    }]}
                    vacancyData={allVacancies.find(v => v.id === safeVacancyId)}
                    onSuccessCallback={() => {
                        reloadCommunications();
                    }}
                />
            )}
        </div>,
        document.body
    );
};

const NavButton = ({ icon, label, count, active, onClick, color = 'text-red-' }: any) => (
    <button onClick={onClick} className={`text-left px-3 py-2.5 rounded-lg font-bold text-xs transition-all flex items-center gap-3 group ${active ? `bg-white ${color} shadow-sm ring-1 ring-slate-200` : 'text-slate-500 hover:bg-slate-100'}`}>
        <span className={active ? color : 'text-slate-400 group-hover:text-slate-600'}>{icon}</span>
        {label}
        {count !== undefined && <span className="ml-auto bg-slate-200 text-slate-600 text-[9px] px-1.5 py-0.5 rounded-md font-black">{count}</span>}
    </button>
);

const SectionHeader = ({ icon, title }: any) => (
    <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-4 flex items-center gap-2 border-b border-slate-100 pb-2">
        {icon} {title}
    </h3>
);

const MailBuilderPopup = ({ onClose, candidateName, candidateEmail }: any) => {
    const [prompt, setPrompt] = useState('');
    const [isGenerating, setIsGenerating] = useState(false);
    const [hasGenerated, setHasGenerated] = useState(false);
    const [subject, setSubject] = useState('Bericht van ons');

    const editor = useEditor({
        extensions: [StarterKit],
        content: '<p>Beste ' + (candidateName?.split(' ')[0] || 'kandidaat') + ',</p><p><br></p>',
        editorProps: {
            attributes: {
                className: 'prose prose-sm prose-slate focus:outline-none min-h-[200px] outline-none w-full bg-white p-4 rounded-b-xl border border-t-0 border-slate-200'
            }
        }
    });

    const handleGenerate = async () => {
        if (!prompt.trim() || !editor) return;
        setIsGenerating(true);
        try {
            const aiPrompt = `Context: Je mailt kandidaat "${candidateName}". Instructie recruiter: ${prompt}. Schrijf de mail professioneel, vlot en kort. Gebruik geen placeholders, verzin logische tekst of houd het algemeen.`;
            const result = await generateTemplateContent(aiPrompt, 'email');
            
            if (result && result.content) {
                // Ensure proper HTML lines
                const htmlContent = result.content.split('\n').map((line: string) => `<p>${line || '<br>'}</p>`).join('');
                editor.commands.setContent(htmlContent);
                if (result.subject) setSubject(result.subject);
                setHasGenerated(true);
            } else {
                alert("Kon geen mail genereren.");
            }
        } catch(e) {
            console.error(e);
            alert("Genereren mislukt.");
        } finally {
            setIsGenerating(false);
        }
    };

    const handleOpenInM365 = () => {
        if (!candidateEmail || !editor) return;
        // Strip HTML for mailto by getting pure text content
        const rawText = editor.getText();
        const mailtoUrl = `mailto:${candidateEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(rawText)}`;
        window.location.href = mailtoUrl;
        onClose();
    };

    return createPortal(
        <div className="fixed inset-0 z-[120] flex items-center justify-center bg-slate-900/40 backdrop-blur-sm p-4 animate-fadeIn">
            <div className="bg-white w-full max-w-2xl rounded-2xl shadow-2xl flex flex-col overflow-hidden animate-scaleIn max-h-[90vh]">
                <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between shrink-0">
                    <h3 className="text-sm font-black text-slate-900 uppercase tracking-widest flex items-center gap-2">
                        <Sparkles size={16} className="text-indigo-600" /> AI Mail Builder - {candidateName}
                    </h3>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-600 p-1"><X size={18} /></button>
                </div>
                <div className="p-6 overflow-y-auto custom-scrollbar flex flex-col gap-4">
                    <div className="shrink-0">
                        <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2 block">1. Instructie voor Froukje AI</label>
                        <div className="flex gap-2">
                            <input
                                type="text"
                                value={prompt}
                                onChange={e => setPrompt(e.target.value)}
                                onKeyDown={e => e.key === 'Enter' && handleGenerate()}
                                placeholder="Bijv. Nodig uit voor eerste online kennismaking..."
                                className="flex-1 bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none transition-all"
                            />
                            <button 
                                onClick={handleGenerate}
                                disabled={!prompt.trim() || isGenerating}
                                className="bg-indigo-600 text-white text-xs font-bold px-4 rounded-xl hover:bg-indigo-700 disabled:opacity-50 transition-colors flex items-center gap-2 shrink-0"
                            >
                                {isGenerating ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} 
                                Genereer
                            </button>
                        </div>
                    </div>

                    <div className="flex-1 flex flex-col min-h-[300px]">
                        <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2 block">2. Rich Text Editor</label>
                        <div className="flex flex-col flex-1 shadow-sm rounded-xl">
                            <div className="bg-slate-50 border border-slate-200 rounded-t-xl p-2 flex items-center gap-3">
                                <span className="text-xs font-bold text-slate-500 px-2">Onderwerp:</span>
                                <input 
                                    className="flex-1 bg-transparent text-sm font-bold text-slate-900 outline-none placeholder:font-normal"
                                    value={subject}
                                    onChange={e => setSubject(e.target.value)}
                                    placeholder="Mail onderwerp"
                                />
                            </div>
                            <div className="flex-1 flex flex-col relative h-[300px] overflow-y-auto">
                                <EditorContent editor={editor} className="flex-1" />
                            </div>
                        </div>
                    </div>

                    <div className="flex justify-end pt-4 border-t border-slate-100 shrink-0">
                        <button 
                            onClick={handleOpenInM365}
                            disabled={!candidateEmail}
                            className="bg-[#0f6cbd] text-white text-xs font-bold px-5 py-2.5 rounded-xl hover:bg-blue-800 transition-colors flex items-center gap-2 shadow-sm disabled:opacity-50"
                        >
                            <Mail size={16} /> Open in Outlook / M365
                        </button>
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
};

export const WhatsAppBuilderPopup = ({ onClose, candidateName, candidatePhone, candidateId, userId: initialUserId }: any) => {
    const [prompt, setPrompt] = useState('');
    const [isGenerating, setIsGenerating] = useState(false);
    const [message, setMessage] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [userId, setUserId] = useState(initialUserId);

    useEffect(() => {
        if (!userId) {
            supabase.auth.getUser().then(({ data }: any) => {
                if (data?.user) setUserId(data.user.id);
            });
        }
    }, [userId]);

    const handleGenerate = async () => {
        if (!prompt.trim()) return;
        setIsGenerating(true);
        try {
            const aiPrompt = `Context: Je appt kandidaat "${candidateName}". Instructie recruiter: ${prompt}. Schrijf de whatsapp kort, informeel en krachtig (max 3-4 zinnen). Gebruik geen placeholders of hashtags.`;
            const result = await generateTemplateContent(aiPrompt, 'sms');
            
            if (result && result.content) {
                setMessage(result.content);
            } else {
                alert("Kon geen bericht genereren.");
            }
        } catch(e) {
            console.error(e);
            alert("Genereren mislukt.");
        } finally {
            setIsGenerating(false);
        }
    };

    const handleSendDirectly = async () => {
        if (!candidatePhone) return;
        let phone = candidatePhone.replace(/[^0-9+]/g, '');
        if (phone.startsWith('0')) {
            phone = '+31' + phone.substring(1);
        }
        const encodedMessage = encodeURIComponent(message);
        const url = `https://wa.me/${phone}?text=${encodedMessage}`;
        window.open(url, '_blank');
        
        // Save as sent note
        if (candidateId && userId) {
             const { error } = await supabase.from('candidate_communications').insert({
                 profile_id: candidateId,
                 user_id: userId,
                 type: 'whatsapp (verzonden via web)',
                 content: `✅ WhatsApp Concept Geopend:\n\n${message}`
             });
             if (error) console.warn("Failed to save WA comm", error);
        }
        onClose();
    };

    const handleSaveToOutbox = async () => {
        if (!message) return;
        setIsSaving(true);
        try {
            await supabase.from('todos').insert({
                title: `[OUTBOX] WhatsApp: ${candidateName}`,
                description: message,
                status: 'Pending',
                priority: 'medium',
                user_id: userId,
                candidate_id: candidateId,
                quick_link_url: window.location.pathname + window.location.search,
                quick_link_title: `Kandidaat: ${candidateName}`
            });
            alert("Bericht staat klaar om verzonden te worden in het Postvak UIT!");
            onClose();
        } catch (e) {
            console.error(e);
            alert("Opslaan mislukt.");
        } finally {
            setIsSaving(false);
        }
    };

    return createPortal(
        <div className="fixed inset-0 z-[120] flex items-center justify-center bg-slate-900/40 backdrop-blur-sm p-4 animate-fadeIn">
            <div className="bg-white w-full max-w-lg rounded-2xl shadow-2xl flex flex-col overflow-hidden animate-scaleIn">
                <div className="px-6 py-4 border-b border-green-50 bg-green-50/30 flex items-center justify-between shrink-0">
                    <h3 className="text-sm font-black text-slate-900 uppercase tracking-widest flex items-center gap-2">
                        <MessageSquare size={16} className="text-green-600" /> AI WhatsApp Builder - {candidateName}
                    </h3>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-600 p-1"><X size={18} /></button>
                </div>
                <div className="p-6 flex flex-col gap-4">
                    <div className="shrink-0">
                        <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2 block">1. Instructie voor Froukje AI</label>
                        <div className="flex gap-2">
                            <input
                                type="text"
                                value={prompt}
                                onChange={e => setPrompt(e.target.value)}
                                onKeyDown={e => e.key === 'Enter' && handleGenerate()}
                                placeholder="Bijv. Vraag of kandidaat donderdag kan bellen..."
                                className="flex-1 bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:border-green-500 focus:ring-1 focus:ring-green-500 outline-none transition-all"
                            />
                            <button 
                                onClick={handleGenerate}
                                disabled={!prompt.trim() || isGenerating}
                                className="bg-green-600 text-white text-xs font-bold px-4 rounded-xl hover:bg-green-700 disabled:opacity-50 transition-colors flex items-center gap-2 shrink-0"
                            >
                                {isGenerating ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} 
                                Genereer
                            </button>
                        </div>
                    </div>

                    <div className="flex-1 flex flex-col">
                        <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2 block">2. Concept Bericht</label>
                        <textarea
                            value={message}
                            onChange={e => setMessage(e.target.value)}
                            placeholder="Genereer een bericht of typ hier..."
                            className="w-full h-32 bg-slate-50 border border-slate-200 rounded-xl p-3 text-sm focus:border-green-500 outline-none transition-all resize-none custom-scrollbar"
                        />
                    </div>

                    <div className="flex justify-between items-center pt-2 mt-2 gap-3 shrink-0">
                        <button 
                            onClick={handleSaveToOutbox}
                            disabled={!message.trim() || isSaving}
                            className="bg-slate-100 text-slate-700 text-xs font-bold px-4 py-2.5 rounded-xl hover:bg-slate-200 transition-colors flex items-center gap-2 disabled:opacity-50 flex-1 justify-center"
                        >
                            {isSaving ? <Loader2 size={14} className="animate-spin" /> : <Inbox size={14} />} Zet klaar in outbox
                        </button>
                        <button 
                            onClick={handleSendDirectly}
                            disabled={!message.trim() || !candidatePhone}
                            className="bg-[#25D366] text-white text-xs font-bold px-4 py-2.5 rounded-xl hover:bg-[#1DA851] transition-colors flex items-center gap-2 shadow-sm disabled:opacity-50 flex-1 justify-center"
                        >
                            <MessageSquare size={14} /> Verstuur gelijk (Web)
                        </button>
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default CandidateDetailModal;
